var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, {
  get: (a, b) => (typeof require !== "undefined" ? require : a)[b]
}) : x)(function(x) {
  if (typeof require !== "undefined") return require.apply(this, arguments);
  throw Error('Dynamic require of "' + x + '" is not supported');
});

// src/RetreatManager.ts
import {
  PersistenceTier as PersistenceTier2
} from "@retreat/core";
import { createChildLogger as createChildLogger23 } from "@retreat/shared";
import { createActor } from "xstate";

// src/utils/TypedEmitter.ts
var TypedEmitter = class {
  _closed = false;
  eventListeners = /* @__PURE__ */ new Map();
  /** INV-020: Close the bus. All subsequent emit() and on() calls are no-ops. Idempotent. */
  close() {
    this._closed = true;
  }
  get isClosed() {
    return this._closed;
  }
  /**
   * Register an event listener
   */
  on(event, handler) {
    if (this._closed) return this;
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, /* @__PURE__ */ new Set());
    }
    this.eventListeners.get(event).add(handler);
    return this;
  }
  /**
   * Register a one-time event listener
   */
  once(event, handler) {
    const wrappedHandler = (...args) => {
      handler(...args);
      this.off(event, wrappedHandler);
    };
    return this.on(event, wrappedHandler);
  }
  /**
   * Remove an event listener
   */
  off(event, handler) {
    if (!handler) {
      this.eventListeners.delete(event);
      return this;
    }
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.eventListeners.delete(event);
      }
    }
    return this;
  }
  /**
   * Emit an event to all listeners
   */
  emit(event, ...args) {
    if (this._closed) return false;
    const handlers = this.eventListeners.get(event);
    if (!handlers || handlers.size === 0) {
      return false;
    }
    const handlersCopy = Array.from(handlers);
    for (const handler of handlersCopy) {
      try {
        handler(...args);
      } catch (error) {
        try {
          const { createChildLogger: createChildLogger26 } = __require("@retreat/shared");
          const log = createChildLogger26("TypedEmitter");
          log.error(`Error in listener for event "${String(event)}":`, error);
        } catch {
        }
      }
    }
    return true;
  }
  /**
   * Remove all listeners for an event or all events
   */
  removeAllListeners(event) {
    if (event) {
      this.eventListeners.delete(event);
    } else {
      this.eventListeners.clear();
    }
    return this;
  }
  /**
   * Get listener count for an event
   */
  listenerCount(event) {
    const handlers = this.eventListeners.get(event);
    return handlers ? handlers.size : 0;
  }
  /**
   * Get all listeners for an event
   */
  listeners(event) {
    const handlers = this.eventListeners.get(event);
    return handlers ? Array.from(handlers) : [];
  }
  /**
   * Get or create listeners array for event
   */
  rawListeners(event) {
    return this.listeners(event);
  }
};

// src/orchestration/EventOrchestrator.ts
import { createChildLogger as createChildLogger2 } from "@retreat/shared";

// src/types/retreat-state.ts
var RetreatState = /* @__PURE__ */ ((RetreatState2) => {
  RetreatState2["IDLE"] = "IDLE";
  RetreatState2["INITIALIZING"] = "INITIALIZING";
  RetreatState2["RUNNING"] = "RUNNING";
  RetreatState2["PAUSED"] = "PAUSED";
  RetreatState2["COMPLETED"] = "COMPLETED";
  RetreatState2["ERROR"] = "ERROR";
  return RetreatState2;
})(RetreatState || {});

// src/session/RetreatSession.ts
import { createChildLogger } from "@retreat/shared";
var RetreatSession = class {
  constructor(_retreat, progress, orchestrator) {
    this._retreat = _retreat;
    this.progress = progress;
    this.orchestrator = orchestrator;
  }
  _retreat;
  progress;
  orchestrator;
  log = createChildLogger("RetreatSession");
  get retreat() {
    return this._retreat;
  }
  saveState() {
    this.log.flow("saveState", { retreatId: this._retreat.id });
    return {
      progressPointer: this.progress,
      clockOffsetSeconds: 0,
      retreatId: this._retreat.id
    };
  }
  restore(state) {
    throw new Error(
      "RetreatSession.restore() is not implemented \u2014 use generateAndHydrate()"
    );
  }
};

// src/orchestration/EventOrchestrator.ts
var GAP_THRESHOLD_SECONDS = 30;
var EventOrchestrator = class {
  constructor(timelineGenerator, eventQueue, engine, clock, dag) {
    this.timelineGenerator = timelineGenerator;
    this.eventQueue = eventQueue;
    this.engine = engine;
    this.clock = clock;
    this.dag = dag;
  }
  timelineGenerator;
  eventQueue;
  engine;
  clock;
  dag;
  lastElapsed = 0;
  _hasReceivedTick = false;
  log = createChildLogger2("EventOrchestrator");
  _onComplete = null;
  _completionFired = false;
  /**
   * Callback invoked with events that were skipped during a clock gap.
   * Wired by RetreatManager to mark them in IdempotencyManager.
   */
  _onGapSkip = null;
  setGapSkipCallback(cb) {
    this._onGapSkip = cb;
  }
  /** Wire a one-shot callback fired when the event queue empties after draining. */
  setOnComplete(cb) {
    this._onComplete = cb;
  }
  onTick(elapsed) {
    var _a, _b;
    this.log.trace("onTick", { elapsed });
    const prevElapsed = this.lastElapsed;
    this.lastElapsed = elapsed;
    if (this._hasReceivedTick && elapsed - prevElapsed > GAP_THRESHOLD_SECONDS) {
      const skipped = this.eventQueue.seek(elapsed);
      if (skipped.length > 0) {
        this.log.flow("onTick: gap detected, skipping events", {
          prevElapsed: Math.floor(prevElapsed),
          elapsed: Math.floor(elapsed),
          skippedCount: skipped.length
        });
        (_a = this._onGapSkip) == null ? void 0 : _a.call(this, skipped);
      }
    }
    this._hasReceivedTick = true;
    const drained = this._drainReady(elapsed);
    const isFirstQueueDrain = drained > 0 && this.eventQueue.isEmpty() && !this._completionFired;
    if (isFirstQueueDrain) {
      this._completionFired = true;
      this.log.flow("onTick: all events exhausted \u2014 firing onComplete");
      (_b = this._onComplete) == null ? void 0 : _b.call(this);
    }
  }
  rehydrate(fromOffset) {
    this.log.trace("rehydrate", { fromOffset });
    const currentEvents = [];
    while (!this.eventQueue.isEmpty()) {
      const event = this.eventQueue.pop();
      if (event && event.offsetSeconds >= fromOffset) {
        currentEvents.push(event);
      }
    }
    for (const event of currentEvents) {
      this.eventQueue.enqueue(event);
    }
  }
  inject(event, shouldFlush = true) {
    const events = Array.isArray(event) ? event : [event];
    for (const e of events) {
      this.log.flow("inject", {
        eventId: e.id,
        offsetSeconds: e.offsetSeconds,
        lastElapsed: this.lastElapsed
      });
      this.eventQueue.enqueue(e);
    }
    if (shouldFlush) {
      this.executeManualFlush();
    }
  }
  flush() {
    if (this.eventQueue.isEmpty()) return;
    const ready = this.eventQueue.peekReady(this.lastElapsed);
    if (!ready) return;
    this.log.trace("flush executeManualFlush");
    this.executeManualFlush();
  }
  executeManualFlush() {
    this._drainReady(this.lastElapsed);
  }
  _drainReady(elapsed) {
    let drained = 0;
    while (!this.eventQueue.isEmpty()) {
      const event = this.eventQueue.peekReady(elapsed);
      if (!event) {
        this.log.trace("drain complete", { elapsed, drained });
        break;
      }
      if (event.plane === "overlay") {
        this.log.info("draining overlay event", {
          eventId: event.id,
          offsetSeconds: event.offsetSeconds,
          plane: event.plane
        });
      } else {
        this.log.trace("draining event", {
          eventId: event.id,
          offsetSeconds: event.offsetSeconds,
          plane: event.plane
        });
      }
      try {
        this.engine.execute(event);
        this.eventQueue.pop();
        drained++;
      } catch (error) {
        this.log.trace("drain error", { eventId: event.id, error });
        this.eventQueue.pop();
      }
    }
    return drained;
  }
};

// src/factories/RetreatSessionFactory.ts
import {
  EditableRetreatScheduleSchema,
  scheduleToDAG,
  VersionedRetreat,
  Day,
  ProgressPointer
} from "@retreat/core";
import { createChildLogger as createChildLogger7 } from "@retreat/shared";

// src/orchestration/PriorityEventQueue.ts
import { RuntimeEventOrigin } from "@retreat/core";
import { createChildLogger as createChildLogger3 } from "@retreat/shared";
import {
  ORIGIN_WEIGHT_MANUAL,
  ORIGIN_WEIGHT_SYSTEM,
  ORIGIN_WEIGHT_SCHEDULED
} from "@retreat/core";
import { PriorityQueue } from "@datastructures-js/priority-queue";
var PriorityEventQueue = class {
  queue;
  log = createChildLogger3("PriorityEventQueue");
  constructor() {
    this.queue = new PriorityQueue(
      (a, b) => this.compare(a, b)
    );
  }
  compare(a, b) {
    if (a.offsetSeconds !== b.offsetSeconds) {
      return a.offsetSeconds - b.offsetSeconds;
    }
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    const originWeights = {
      [RuntimeEventOrigin.Manual]: ORIGIN_WEIGHT_MANUAL,
      [RuntimeEventOrigin.System]: ORIGIN_WEIGHT_SYSTEM,
      [RuntimeEventOrigin.Scheduled]: ORIGIN_WEIGHT_SCHEDULED
    };
    const weightA = originWeights[a.origin] ?? ORIGIN_WEIGHT_SCHEDULED;
    const weightB = originWeights[b.origin] ?? ORIGIN_WEIGHT_SCHEDULED;
    return weightA - weightB;
  }
  hydrate(events, resumeOffset = 0) {
    this.log.info(
      "hydrate START eventCount=%d resumeOffset=%d",
      events.length,
      resumeOffset
    );
    let startIdx = 0;
    if (resumeOffset > 0) {
      let left = 0, right = events.length - 1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (events[mid].offsetSeconds < resumeOffset) {
          left = mid + 1;
        } else {
          startIdx = mid;
          right = mid - 1;
        }
      }
    }
    this.queue = new PriorityQueue(
      (a, b) => this.compare(a, b)
    );
    for (let i = startIdx; i < events.length; i++) {
      this.queue.enqueue(events[i]);
    }
    const nextEvent = this.queue.front();
    this.log.info(
      "hydrate END eventCount=%d resumeOffset=%d nextEventAt=%d",
      events.length - startIdx,
      resumeOffset,
      nextEvent ? nextEvent.offsetSeconds : -1
    );
  }
  peek() {
    return this.queue.isEmpty() ? null : this.queue.front();
  }
  peekReady(nowOffset) {
    const event = this.peek();
    const ready = event && event.offsetSeconds <= nowOffset ? event : null;
    if (ready) {
      this.log.trace(
        "peekReady nowOffset=%d ready=true eventId=%s offsetSeconds=%d",
        nowOffset,
        ready.id,
        ready.offsetSeconds
      );
    } else {
      this.log.trace("peekReady", { nowOffset, ready: false });
    }
    return ready;
  }
  pop() {
    return this.queue.isEmpty() ? null : this.queue.dequeue();
  }
  enqueue(event) {
    this.log.trace(
      "enqueue eventId=%s offsetSeconds=%d",
      event.id,
      event.offsetSeconds
    );
    this.queue.enqueue(event);
  }
  /**
   * Drain and return all events whose offsetSeconds < toOffsetExclusive.
   * Events AT toOffsetExclusive are left in the queue and will fire normally.
   * Used for gap-skip: when the clock jumps (device sleep), events that fell
   * in the gap are drained and marked executed to prevent catch-up bell bursts.
   */
  seek(toOffsetExclusive) {
    const skipped = [];
    while (!this.queue.isEmpty()) {
      const front = this.queue.front();
      if (!front || front.offsetSeconds >= toOffsetExclusive) break;
      const event = this.queue.dequeue();
      if (event) skipped.push(event);
    }
    if (skipped.length > 0) {
      this.log.flow("seek: drained events before offset", {
        skippedCount: skipped.length,
        toOffsetExclusive
      });
    }
    return skipped;
  }
  isEmpty() {
    return this.queue.isEmpty();
  }
  size() {
    return this.queue.size();
  }
};

// src/orchestration/TimelineGenerator.ts
import {
  TransformError,
  ErrorCode,
  RuntimeEventOrigin as RuntimeEventOrigin2
} from "@retreat/core";
import { createChildLogger as createChildLogger4 } from "@retreat/shared";

// src/orchestration/topo-sort.ts
var TopologicalSort = class {
  nodes;
  edges = /* @__PURE__ */ new Map();
  inDegree = /* @__PURE__ */ new Map();
  constructor(nodeMap) {
    this.nodes = new Map(nodeMap);
    for (const key of this.nodes.keys()) {
      this.inDegree.set(key, 0);
    }
  }
  /**
   * Add a directed edge from 'from' to 'to'
   * Meaning: 'from' must come before 'to'
   */
  addEdge(from, to) {
    if (!this.nodes.has(from) || !this.nodes.has(to)) {
      throw new Error(`Edge nodes must exist in graph. from=${from}, to=${to}`);
    }
    if (!this.edges.has(from)) {
      this.edges.set(from, /* @__PURE__ */ new Set());
    }
    if (!this.edges.get(from).has(to)) {
      this.edges.get(from).add(to);
      this.inDegree.set(to, (this.inDegree.get(to) || 0) + 1);
    }
  }
  /**
   * Kahn's algorithm for topological sorting
   * Returns sorted Map<K, V> in topological order
   */
  sort() {
    const sorted = /* @__PURE__ */ new Map();
    const queue = [];
    for (const [key, degree] of this.inDegree.entries()) {
      if (degree === 0) {
        queue.push(key);
      }
    }
    while (queue.length > 0) {
      const current = queue.shift();
      sorted.set(current, this.nodes.get(current));
      const neighbors = this.edges.get(current);
      if (neighbors) {
        for (const neighbor of neighbors) {
          this.inDegree.set(neighbor, this.inDegree.get(neighbor) - 1);
          if (this.inDegree.get(neighbor) === 0) {
            queue.push(neighbor);
          }
        }
      }
    }
    if (sorted.size !== this.nodes.size) {
      throw new Error("Topological sort failed: cycle detected in graph");
    }
    return sorted;
  }
};

// src/orchestration/TimelineGenerator.ts
var EDGE_TYPES = {
  SEQUENTIAL: "sequential",
  PARALLEL: "parallel",
  OFFSET: "offset"
};
var TimelineGenerator = class {
  log = createChildLogger4("TimelineGenerator");
  // TODO: Convert to truly async when adding I/O operations (asset loading, etc.)
  generateAsync(dag) {
    this.log.info(
      "generateAsync START retreatId=%s nodes=%d",
      dag.retreatId,
      dag.nodes.length
    );
    try {
      const nodeById = /* @__PURE__ */ new Map();
      dag.nodes.forEach((node) => {
        nodeById.set(node.id, node);
      });
      const sorter = new TopologicalSort(nodeById);
      dag.edges.forEach((edge) => {
        sorter.addEdge(edge.fromNodeId, edge.toNodeId);
      });
      const sortedNodeIds = [...sorter.sort().keys()];
      const edgesByTarget = /* @__PURE__ */ new Map();
      dag.edges.forEach((edge) => {
        if (!edgesByTarget.has(edge.toNodeId)) {
          edgesByTarget.set(edge.toNodeId, []);
        }
        edgesByTarget.get(edge.toNodeId).push(edge);
      });
      const nodeStartTimes = /* @__PURE__ */ new Map();
      nodeStartTimes.set(dag.entryNodeId, 0);
      sortedNodeIds.forEach((nodeId) => {
        if (nodeId === dag.entryNodeId) return;
        const incomingEdges = edgesByTarget.get(nodeId) || [];
        if (incomingEdges.length === 0) {
          nodeStartTimes.set(nodeId, 0);
          return;
        }
        let nodeStartTime = 0;
        incomingEdges.forEach((edge) => {
          const parentNode = nodeById.get(edge.fromNodeId);
          const parentStartTime = nodeStartTimes.get(edge.fromNodeId) || 0;
          let edgeStartTime = parentStartTime;
          if (edge.type === EDGE_TYPES.SEQUENTIAL) {
            edgeStartTime = parentStartTime + ((parentNode == null ? void 0 : parentNode.durationSeconds) || 0);
          } else if (edge.type === EDGE_TYPES.OFFSET) {
            const offset = edge.offsetSeconds || 0;
            if (offset < 0) {
              throw new TransformError(
                ErrorCode.NEGATIVE_OFFSET,
                `Negative offset not allowed: ${offset}s on edge ${edge.fromNodeId} -> ${nodeId}`
              );
            }
            edgeStartTime = parentStartTime + offset;
          }
          nodeStartTime = Math.max(nodeStartTime, edgeStartTime);
        });
        nodeStartTimes.set(nodeId, nodeStartTime);
      });
      const runtimeEvents = [];
      for (const nodeId of sortedNodeIds) {
        const node = nodeById.get(nodeId);
        const offsetSeconds = nodeStartTimes.get(nodeId) || 0;
        this.log.trace(
          "[%s] @ T+%ds (Duration: %ds)",
          node.id,
          offsetSeconds,
          node.durationSeconds
        );
        runtimeEvents.push({
          id: nodeId,
          offsetSeconds,
          plane: node.plane,
          priority: 0,
          payload: node,
          origin: RuntimeEventOrigin2.System,
          isIdempotent: true,
          // Flatten node properties for direct access
          type: node.type,
          durationSeconds: node.durationSeconds,
          containerId: node.containerId
        });
      }
      runtimeEvents.sort((a, b) => a.offsetSeconds - b.offsetSeconds);
      this.log.info("generateAsync END", { eventsCount: runtimeEvents.length });
      return Promise.resolve(runtimeEvents);
    } catch (error) {
      throw new TransformError(
        ErrorCode.DAG_TRAVERSAL_FAILED,
        `Timeline generation failed: ${error.message}`,
        false
      );
    }
  }
};

// src/orchestration/CommandQueue.ts
import { createChildLogger as createChildLogger5 } from "@retreat/shared";
var CommandQueue = class {
  queue = [];
  log = createChildLogger5("CommandQueue");
  enqueue(cmd) {
    this.log.trace("enqueue", { command: cmd.constructor.name });
    this.queue.push(cmd);
  }
  drain() {
    this.log.trace("drain", { queueLength: this.queue.length });
    this.queue = [];
  }
};

// src/execution/ExecutionEngine.ts
import { createChildLogger as createChildLogger6 } from "@retreat/shared";

// src/types/engine-events.ts
var ENGINE_EVENTS = {
  STATE_CHANGE: "state:change",
  TICK: "tick",
  EXECUTION_SUCCESS: "execution:success",
  EXECUTION_ERROR: "execution:error",
  SYNC: "sync",
  GAP_SKIP_EXECUTED: "gap_skip:executed",
  LOW_BATTERY_WARNING: "battery:low_warning",
  LOW_BATTERY_CRITICAL: "battery:low_critical",
  CHARGING_RESUMED: "battery:charging_resumed",
  STORAGE_QUOTA_WARNING: "storage:quota_warning",
  RESILIENCE_WARNING: "resilience:warning",
  PERSISTENCE_ERROR: "persistence:error",
  CONNECTIVITY_ONLINE: "connectivity:online",
  CONNECTIVITY_OFFLINE: "connectivity:offline"
};

// src/execution/ExecutionEngine.ts
var ExecutionEngine = class extends TypedEmitter {
  log = createChildLogger6("ExecutionEngine");
  planes = [];
  idempotencyChecker = null;
  setIdempotencyChecker(checker) {
    this.idempotencyChecker = checker;
  }
  attachPlane(plane) {
    this.log.flow("attachPlane", { planeName: plane.constructor.name });
    this.planes.push(plane);
  }
  process(events) {
    this.log.flow("process", { eventCount: events.length });
  }
  execute(event) {
    var _a;
    this.log.flow("execute event", {
      eventId: event.id,
      offsetSeconds: event.offsetSeconds,
      plane: event.plane
    });
    if ((_a = this.idempotencyChecker) == null ? void 0 : _a.hasBeenExecuted(event.id)) {
      this.log.warn("execute SKIPPED (already executed)", {
        eventId: event.id,
        plane: event.plane
      });
      this.emit(ENGINE_EVENTS.EXECUTION_SUCCESS, event);
      return;
    }
    try {
      for (const plane of this.planes) {
        this.log.trace("execute dispatching to plane", {
          planeName: plane.constructor.name
        });
        plane.execute(event);
      }
      this.emit(ENGINE_EVENTS.EXECUTION_SUCCESS, event);
    } catch (error) {
      this.log.trace("execute error", { error });
      this.emit(ENGINE_EVENTS.EXECUTION_ERROR, { event, error });
      throw error;
    }
  }
  interrupt() {
    this.log.trace("interrupt");
  }
};

// src/factories/RetreatSessionFactory.ts
var RetreatSessionFactory = class {
  constructor(clock) {
    this.clock = clock;
  }
  clock;
  log = createChildLogger7("RetreatSessionFactory");
  /**
   * Create a RetreatSession from an EditableRetreatSchedule.
   *
   * Process:
   * 1. Validate schedule against Zod schema
   * 2. Transform to ExecutionDAG via scheduleToDAG
   * 3. Convert to VersionedRetreat class instance
   * 4. Assemble RetreatSession with orchestrator
   */
  create(schedule) {
    var _a, _b;
    const containerCount = schedule.days.reduce(
      (sum, d) => sum + d.containers.length,
      0
    );
    this.log.info(
      "create START schedule=%s containers=%d",
      schedule.id,
      containerCount
    );
    const validated = EditableRetreatScheduleSchema.parse(schedule);
    const dagResult = scheduleToDAG(validated);
    this.log.trace(
      "DAG created nodes=%d edges=%d",
      dagResult.dag.nodes.length,
      dagResult.dag.edges.length
    );
    const retreat = new VersionedRetreat(
      validated.id,
      validated.name,
      validated.version,
      validated.days.map((day) => new Day(day.dayNumber, day.containers)),
      validated.metadata || {}
    );
    const timelineGenerator = new TimelineGenerator();
    const eventQueue = new PriorityEventQueue();
    const engine = new ExecutionEngine();
    const orchestrator = new EventOrchestrator(
      timelineGenerator,
      eventQueue,
      engine,
      this.clock,
      dagResult.dag
    );
    const firstContainerId = ((_b = (_a = retreat.days[0]) == null ? void 0 : _a.containers[0]) == null ? void 0 : _b.id) || "";
    const progress = new ProgressPointer(firstContainerId, 1, 0);
    const session = new RetreatSession(retreat, progress, orchestrator);
    this.log.info("create END", { sessionId: session.retreat.id });
    return session;
  }
  /**
   * Create a RetreatSession from persisted state.
   * Reconstructs session with progress pointer from saved state.
   * Note: Requires the retreat data to be available separately.
   */
  createFromPersistedState(state) {
    throw new Error(
      "createFromPersistedState not yet implemented \u2014 requires retreat schedule data"
    );
  }
};

// src/types/plane-types.ts
var PLANE_TYPES = {
  PRIMARY: "primary",
  OVERLAY: "overlay"
};
var NODE_TYPES = {
  SILENCE: "silence"
};
var VALID_PLANES = Object.values(PLANE_TYPES);
function isValidPlane(value) {
  return VALID_PLANES.includes(value);
}

// src/types/event-classification.ts
var EVENT_CATEGORIES = {
  // Continuous events: emitted many times per execution, safe to repeat
  CONTINUOUS: ["NODE_PROGRESS", "TICK"],
  // Discrete events: emitted once per execution unit, must be deduplicated
  DISCRETE: ["bell", "text", "silence"]
};
function isContinuousEvent(eventType) {
  return EVENT_CATEGORIES.CONTINUOUS.includes(eventType);
}
function isDiscreteEvent(eventType) {
  return EVENT_CATEGORIES.DISCRETE.includes(eventType);
}

// src/types/recovery-actions.ts
var RECOVERY_ACTIONS = {
  NONE: "none",
  SYNC: "sync",
  RESUME: "resume"
};
function isValidRecoveryAction(value) {
  return Object.values(RECOVERY_ACTIONS).includes(value);
}

// src/types/worker-protocol.ts
var WORKER_MESSAGES = {
  TICK: "tick"
};
var WORKER_COMMANDS = {
  START: "start",
  STOP: "stop"
};
function isValidWorkerMessage(value) {
  return Object.values(WORKER_MESSAGES).includes(value);
}
function isValidWorkerCommand(value) {
  return Object.values(WORKER_COMMANDS).includes(value);
}

// src/managers/InternalBus.ts
import { createChildLogger as createChildLogger8 } from "@retreat/shared";
function createInternalBus() {
  const bus = new TypedEmitter();
  const log = createChildLogger8("InternalBus");
  const originalEmit = bus.emit.bind(bus);
  const importantEvents = /* @__PURE__ */ new Set([
    ENGINE_EVENTS.STATE_CHANGE,
    ENGINE_EVENTS.SYNC,
    ENGINE_EVENTS.EXECUTION_SUCCESS,
    ENGINE_EVENTS.EXECUTION_ERROR
  ]);
  bus.emit = function(event, ...args) {
    if (importantEvents.has(event)) {
      log.debug("Bus event emitted", {
        event: String(event),
        handlerCount: this.listenerCount(event),
        timestamp: Date.now()
      });
    }
    return originalEmit(event, ...args);
  };
  return bus;
}

// src/managers/BaseManager.ts
var BaseManager = class {
  eventSubscriptions;
  isReady_ = false;
  isReady() {
    return this.isReady_;
  }
  async shutdown() {
    if (!this.isReady_) return;
    await this._doShutdown();
    this.isReady_ = false;
  }
  /** Override to perform cleanup before the ready flag is cleared. */
  _doShutdown() {
  }
};

// src/managers/PersistenceManager.ts
import { createChildLogger as createChildLogger10 } from "@retreat/shared";
import { PersistenceTier } from "@retreat/core";

// src/modules/PersistenceModule.ts
import { createChildLogger as createChildLogger9 } from "@retreat/shared";
var PersistenceModule = class {
  log = createChildLogger9("PersistenceModule");
  coordinator;
  lastSavedOffset = 0;
  lastSavedEventCount = 0;
  lastVolatileSaveTime = 0;
  VOLATILE_COOLDOWN_MS = 5e3;
  // 5-second throttle
  constructor(coordinator) {
    this.coordinator = coordinator;
  }
  /**
   * Save session with state-change detection and throttling.
   *
   * Smart save logic:
   * - CRITICAL: Always save (no throttle)
   * - HIGH: Save if state changed (offset increased OR events executed)
   * - VOLATILE: Save if state changed AND cooldown elapsed (unless forceSave=true)
   *
   * @param state Current session state
   * @param tier Persistence tier
   * @param forceSave If true, bypass throttle logic (for emergency saves)
   * @returns true if save was performed, false if skipped due to no change/throttle
   */
  async saveSession(state, tier, forceSave = false) {
    const now = Date.now();
    if (tier === 0) {
      await this.coordinator.saveSession(state, tier);
      this.updateLastSaved(state);
      this.log.flow("CRITICAL save", {
        offset: state.lastOffset,
        events: state.executedEventIds.length
      });
      return true;
    }
    const offsetChanged = state.lastOffset > this.lastSavedOffset;
    const eventsChanged = state.executedEventIds.length > this.lastSavedEventCount;
    const stateChanged = offsetChanged || eventsChanged;
    if (!stateChanged) {
      this.log.trace("State unchanged, skipping save");
      return false;
    }
    if (tier === 1) {
      await this.coordinator.saveSession(state, tier);
      this.updateLastSaved(state);
      this.log.flow("HIGH save", {
        offset: state.lastOffset,
        events: state.executedEventIds.length
      });
      return true;
    }
    if (tier === 2) {
      const timeSinceLastVolatileSave = now - this.lastVolatileSaveTime;
      const canSave = timeSinceLastVolatileSave >= this.VOLATILE_COOLDOWN_MS || forceSave;
      if (!canSave) {
        this.log.trace("VOLATILE save throttled", {
          remainingMs: Math.round(
            this.VOLATILE_COOLDOWN_MS - timeSinceLastVolatileSave
          )
        });
        return false;
      }
      await this.coordinator.saveSession(state, tier);
      this.updateLastSaved(state);
      this.lastVolatileSaveTime = now;
      this.log.trace("VOLATILE save executed", {
        offset: state.lastOffset
      });
      return true;
    }
    return false;
  }
  /**
   * Check if session state has changed since last save.
   * Used by lifecycle module to detect unsaved changes before background/pause.
   *
   * @param state Current session state
   * @returns true if offset or event count differs from last saved
   */
  isDirty(state) {
    const offsetChanged = state.lastOffset > this.lastSavedOffset;
    const eventsChanged = state.executedEventIds.length > this.lastSavedEventCount;
    return offsetChanged || eventsChanged;
  }
  /**
   * Load session from coordinator.
   * Called during app initialization or recovery.
   *
   * @param retreatId Retreat identifier
   * @returns Session state or null
   */
  async loadSession(retreatId) {
    const state = await this.coordinator.loadSession(retreatId);
    if (state) {
      this.updateLastSaved(state);
      this.log.trace("Loaded session", { offset: state.lastOffset });
    } else {
      this.log.trace("No session found");
    }
    return state;
  }
  /**
   * Clear session from storage.
   *
   * @param retreatId Retreat identifier
   */
  async clearSession(retreatId) {
    await this.coordinator.clearSession(retreatId);
    this.resetTracking();
    this.log.trace("PersistenceModule: Cleared session", { retreatId });
  }
  /**
   * Update internal tracking after a save.
   * @private
   */
  updateLastSaved(state) {
    this.lastSavedOffset = state.lastOffset;
    this.lastSavedEventCount = state.executedEventIds.length;
  }
  /**
   * Reset tracking (e.g., after clear).
   * @private
   */
  resetTracking() {
    this.lastSavedOffset = 0;
    this.lastSavedEventCount = 0;
    this.lastVolatileSaveTime = 0;
  }
  /**
   * Check if next VOLATILE save would be throttled.
   * Useful for metrics/logging.
   *
   * @returns true if throttled, false if available
   */
  isVolatileThrottled() {
    const now = Date.now();
    const timeSinceLastVolatileSave = now - this.lastVolatileSaveTime;
    return timeSinceLastVolatileSave < this.VOLATILE_COOLDOWN_MS;
  }
  /**
   * Get milliseconds remaining in current VOLATILE cooldown.
   * Returns 0 if no cooldown active.
   *
   * @returns Milliseconds until next VOLATILE save is allowed
   */
  getVolatileCooldownRemaining() {
    const now = Date.now();
    const timeSinceLastVolatileSave = now - this.lastVolatileSaveTime;
    const remaining = this.VOLATILE_COOLDOWN_MS - timeSinceLastVolatileSave;
    return Math.max(0, remaining);
  }
};

// src/managers/PersistenceManager.ts
var PersistenceManager = class _PersistenceManager extends BaseManager {
  constructor(bus, coordinator) {
    super();
    this.bus = bus;
    this.module = new PersistenceModule(coordinator);
  }
  bus;
  name = "PersistenceManager";
  priority = 0;
  log = createChildLogger10(this.name);
  _stopped = false;
  module;
  buildStateFn = null;
  _visibilityCleanup = null;
  /** INV-021: Epoch guard — set by RetreatManager after initialize(). */
  _epochGuard = () => 0;
  setEpochGuard(guard) {
    this._epochGuard = guard;
  }
  /**
   * Golden Thread: The absolute wall-clock timestamp when the retreat started.
   * Captured from SYNC events emitted by HeartbeatManager.
   * Used to anchor the session to real time for drift recovery.
   */
  _goldenThread = {
    absoluteStartTime: 0,
    accumulatedPauseMs: 0
  };
  /**
   * DECLARATIVE SUBSCRIPTIONS: What this manager listens to on the InternalBus.
   * Automatically wired by RetreatManager.registerManager().
   * Automatically unwired during destroy() to prevent memory leaks.
   */
  eventSubscriptions = {
    [ENGINE_EVENTS.EXECUTION_SUCCESS]: () => {
      this.autoPersist("discrete-event");
    },
    [ENGINE_EVENTS.SYNC]: (data) => {
      if (data.absoluteStartTime) {
        this._goldenThread.absoluteStartTime = data.absoluteStartTime;
      }
      this.autoPersist("sync-event");
    },
    [ENGINE_EVENTS.EXECUTION_ERROR]: () => {
      this.autoPersist("execution-error");
    }
  };
  _lastAutoPersistAt = 0;
  static AUTO_PERSIST_DEBOUNCE_MS = 5e3;
  // Tracks the most recent in-flight saveSession promise so stop() can drain
  // it before clearSession() runs — prevents a post-clear write-back race.
  _inflightSave = null;
  autoPersist(context, bypassDebounce = false) {
    if (this._stopped) {
      this.log.trace("autoPersist skipped \u2014 manager stopped", { context });
      return;
    }
    if (!this.buildStateFn) {
      this.log.trace("autoPersist skipped \u2014 no buildStateFn configured", {
        context
      });
      return;
    }
    const now = Date.now();
    if (!bypassDebounce && now - this._lastAutoPersistAt < _PersistenceManager.AUTO_PERSIST_DEBOUNCE_MS) {
      return;
    }
    this._lastAutoPersistAt = now;
    const state = this.buildStateFn();
    if (state) {
      this.log.flow("autoPersist", { context });
      const promise = this.module.saveSession(state, PersistenceTier.HIGH);
      this._inflightSave = promise;
      promise.then(() => {
        if (this._inflightSave === promise) this._inflightSave = null;
      }).catch((err) => {
        var _a;
        this.log.warn("autoPersist failed \u2014 session state not saved", {
          context,
          error: err
        });
        (_a = this.bus) == null ? void 0 : _a.emit(ENGINE_EVENTS.PERSISTENCE_ERROR, {
          context,
          error: err
        });
        if (this._inflightSave === promise) this._inflightSave = null;
      });
    }
  }
  configure(context) {
    if (context == null ? void 0 : context.buildStateFn) this.buildStateFn = context.buildStateFn;
  }
  async initialize() {
    this._stopped = false;
    this._attachVisibilityListener();
    this.log.flow("initialize: persistence layer ready");
    this.isReady_ = true;
  }
  /** INV-023: Suspend autoPersist and clean up listeners. */
  async stop() {
    if (this._visibilityCleanup) {
      this._visibilityCleanup();
      this._visibilityCleanup = null;
    }
    this._stopped = true;
    if (this._inflightSave) {
      await this._inflightSave.catch(() => {
      });
      this._inflightSave = null;
    }
    this.log.flow("stop: autoPersist disabled");
  }
  _doShutdown() {
    this.log.flow("shutdown: persistence cleanup");
  }
  reset() {
    this.log.flow("reset: persistence context cleared");
    this.buildStateFn = null;
    this._stopped = false;
    this.isReady_ = false;
  }
  async saveSession(state, tier) {
    this.log.flow("saveSession", { tier });
    await this.module.saveSession(state, tier);
  }
  /**
   * Save session with CRITICAL tier.
   * Called when page loses visibility to ensure state is persisted
   * before browser/OS can terminate the app.
   */
  async saveCritical(state) {
    this.log.flow("saveCritical: persisting state before visibility loss");
    await this.module.saveSession(state, PersistenceTier.CRITICAL);
  }
  /**
   * Clear session from storage (used when stopping/resetting).
   */
  async clearSession(retreatId) {
    this.log.flow("clearSession", { retreatId });
    const coordinator = this.module.coordinator;
    if (coordinator) {
      return coordinator.clearSession(retreatId);
    }
  }
  /**
   * Attach visibility listener for CRITICAL saves on visibility loss.
   * Triggers immediate persistence when page becomes hidden.
   */
  _attachVisibilityListener() {
    if (typeof document === "undefined") {
      return;
    }
    const capturedEpoch = this._epochGuard ? this._epochGuard() : 0;
    const handler = () => {
      if (this._epochGuard && capturedEpoch !== this._epochGuard()) return;
      if (this._stopped) return;
      if (document.hidden) {
        if (this.buildStateFn) {
          const state = this.buildStateFn();
          if (state) {
            this.saveCritical(state).catch((err) => {
              var _a;
              this.log.flow("saveCritical failed", {
                error: err.message
              });
              (_a = this.bus) == null ? void 0 : _a.emit(ENGINE_EVENTS.PERSISTENCE_ERROR, {
                context: "visibility-save",
                error: err
              });
            });
          }
        }
      }
    };
    document.addEventListener("visibilitychange", handler);
    this._visibilityCleanup = () => {
      document.removeEventListener("visibilitychange", handler);
    };
  }
  /**
   * Load a persisted session from storage.
   * Called by RetreatManager.probe() to detect existing sessions at boot.
   */
  async loadSession(retreatId, version) {
    const coordinator = this.module.coordinator;
    if (coordinator) {
      return coordinator.loadSession(retreatId, version);
    }
    return null;
  }
};

// src/managers/IdempotencyManager.ts
import { createChildLogger as createChildLogger12 } from "@retreat/shared";

// src/orchestration/IdempotencyGuard.ts
import { createChildLogger as createChildLogger11 } from "@retreat/shared";
var IdempotencyGuard = class {
  executedIds = /* @__PURE__ */ new Set();
  onEventExecutedCallback = null;
  log = createChildLogger11("IdempotencyGuard");
  constructor() {
    this.log.info("IdempotencyGuard initialized", {
      className: "IdempotencyGuard",
      methodName: "constructor",
      action: "init"
    });
  }
  /**
   * Restore executed event IDs from persisted session
   *
   * Called after loading session from storage
   *
   * @param executedIds Array of event IDs already executed
   */
  restoreFromSession(executedIds) {
    this.executedIds.clear();
    executedIds.forEach((id) => this.executedIds.add(id));
    this.log.trace("Executed events restored from session", {
      className: "IdempotencyGuard",
      methodName: "restoreFromSession",
      action: "restore",
      count: executedIds.length
    });
  }
  /**
   * Get current set of executed event IDs (for persistence)
   *
   * @returns Array of event IDs (serializable format)
   */
  getExecutedIds() {
    return Array.from(this.executedIds);
  }
  /**
   * Register callback for when an event is executed
   *
   * Used by RetreatManager to trigger session save
   *
   * @param callback Function to call with eventId
   */
  onEventExecuted(callback) {
    this.onEventExecutedCallback = callback;
    this.log.trace("Event execution callback registered", {
      className: "IdempotencyGuard",
      methodName: "onEventExecuted",
      action: "callback_registered"
    });
  }
  /**
   * Check if event has already been executed
   *
   * @param event Event to check
   * @returns true if already executed, false if new
   */
  hasExecuted(event) {
    return this.executedIds.has(event.id);
  }
  /**
   * Mark event as executed
   *
   * Should be called AFTER ExecutionEngine successfully executes the event
   *
   * @param event Event that was executed
   */
  markExecuted(event) {
    if (this.executedIds.has(event.id)) {
      this.log.trace("Event already marked as executed (duplicate call)", {
        className: "IdempotencyGuard",
        methodName: "markExecuted",
        action: "duplicate_mark",
        eventId: event.id,
        eventType: event.type
      });
      return;
    }
    this.executedIds.add(event.id);
    this.log.trace("Event marked as executed", {
      className: "IdempotencyGuard",
      methodName: "markExecuted",
      action: "executed",
      eventId: event.id,
      eventType: event.type,
      totalExecuted: this.executedIds.size
    });
    if (this.onEventExecutedCallback) {
      this.onEventExecutedCallback(event.id);
    }
  }
  /**
   * Should executor run this event?
   *
   * Returns false if already executed (idempotency skip)
   * Returns true if new (proceed with execution)
   *
   * @param event Event to check
   * @returns true if should execute, false if should skip
   */
  shouldExecute(event) {
    const shouldRun = !this.hasExecuted(event);
    if (!shouldRun) {
      this.log.flow("Idempotency skip: Event already executed", {
        className: "IdempotencyGuard",
        methodName: "shouldExecute",
        action: "skip_idempotent",
        eventId: event.id,
        eventType: event.type
      });
    }
    return shouldRun;
  }
  /**
   * Mark events in an offset range as executed (skipped).
   * Used when drift is detected to prevent "machine gun" bell bursts.
   *
   * @param fromOffsetSeconds Lower bound (inclusive)
   * @param toOffsetSeconds Upper bound (inclusive)
   */
  markWindowAsSkipped(fromOffsetSeconds, toOffsetSeconds) {
    if (fromOffsetSeconds === toOffsetSeconds) {
      this.log.trace("markWindowAsSkipped: zero-width window, skipping", {
        className: "IdempotencyGuard",
        methodName: "markWindowAsSkipped",
        action: "skip_zero_width",
        offset: fromOffsetSeconds
      });
      return;
    }
    const min = Math.min(fromOffsetSeconds, toOffsetSeconds);
    const max = Math.max(fromOffsetSeconds, toOffsetSeconds);
    const eventIdCount = max - min;
    for (let offset = min; offset <= max; offset++) {
      const eventId = `generated-${offset}`;
      this.executedIds.add(eventId);
      this.executedIds.add(`bell-${offset}s`);
    }
    this.log.flow("markWindowAsSkipped", {
      markedCount: eventIdCount + 1,
      min,
      max
    });
  }
  /**
   * Clear all executed events (for testing or new session)
   */
  reset() {
    this.executedIds.clear();
    this.log.trace("IdempotencyGuard reset", {
      className: "IdempotencyGuard",
      methodName: "reset",
      action: "clear"
    });
  }
  /**
   * Get statistics (for monitoring)
   */
  getStats() {
    return {
      totalExecuted: this.executedIds.size,
      executedIds: this.getExecutedIds()
    };
  }
};

// src/managers/IdempotencyManager.ts
var IdempotencyManager = class extends BaseManager {
  constructor(bus) {
    super();
    this.bus = bus;
    this.guard = new IdempotencyGuard();
  }
  bus;
  name = "IdempotencyManager";
  priority = 5;
  log = createChildLogger12("IdempotencyManager");
  guard;
  _lastOffsetSeconds = 0;
  /**
   * DECLARATIVE SUBSCRIPTIONS: What this manager listens to on the InternalBus.
   * Automatically wired by RetreatManager.registerManager().
   * Automatically unwired during destroy() to prevent memory leaks.
   */
  eventSubscriptions = {
    [ENGINE_EVENTS.EXECUTION_SUCCESS]: (event) => {
      const eventType = event.eventType;
      if (eventType && isContinuousEvent(eventType)) return;
      this.log.flow("markExecuted", { eventId: event.id });
      this.guard.markExecuted(event);
    },
    [ENGINE_EVENTS.SYNC]: (data) => {
      this.handleSync(data);
    },
    [ENGINE_EVENTS.GAP_SKIP_EXECUTED]: (skipped) => {
      for (const event of skipped) {
        this.log.flow("gap-skip markExecuted", { eventId: event.id });
        this.guard.markExecuted(event);
      }
    }
  };
  configure(context) {
  }
  async initialize() {
    this.log.flow("initialize: idempotency guard ready");
    this.isReady_ = true;
  }
  /** INV-023: No active loops to suspend. */
  async stop() {
  }
  _doShutdown() {
    this.log.flow("shutdown: clearing idempotency state");
  }
  reset() {
    this.log.flow("reset: clearing executed event IDs");
    this.guard.reset();
    this._lastOffsetSeconds = 0;
    this.isReady_ = false;
  }
  markExecuted(event) {
    this.guard.markExecuted(event);
  }
  hasExecuted(event) {
    return this.guard.hasExecuted(event);
  }
  hasBeenExecuted(eventId) {
    return this.guard.getExecutedIds().includes(eventId);
  }
  getExecutedIds() {
    return this.guard.getExecutedIds();
  }
  restoreFromSession(eventIds) {
    this.log.flow("restoreFromSession", { count: eventIds.length });
    this.guard.restoreFromSession(eventIds);
  }
  /**
   * Mark events in an offset range as executed (skipped).
   * Called by SYNC handler when drift is detected.
   * Prevents "machine gun" bell bursts after resuming from backgrounding.
   */
  markWindowAsSkipped(fromOffsetSeconds, toOffsetSeconds) {
    this.guard.markWindowAsSkipped(fromOffsetSeconds, toOffsetSeconds);
  }
  /**
   * Handle SYNC event: Mark jump window as skipped on drift SYNCs only.
   * Restore SYNCs (no driftMs) just update internal offset tracking without
   * marking anything, since executedEventIds are already restored from session.
   */
  handleSync(data) {
    const newOffset = data.offsetSeconds;
    if (data.driftMs === void 0) {
      this._lastOffsetSeconds = newOffset;
      return;
    }
    const oldOffset = this._lastOffsetSeconds;
    if (newOffset === oldOffset) {
      this.log.trace("handleSync: zero-width offset change, skipping", {
        className: "IdempotencyManager",
        methodName: "handleSync",
        action: "skip_zero_drift"
      });
      this._lastOffsetSeconds = newOffset;
      return;
    }
    const jumpMagnitude = Math.abs(newOffset - oldOffset);
    this.log.flow("handleSync: drift detected", {
      jump: jumpMagnitude,
      minOffset: Math.min(oldOffset, newOffset),
      maxOffset: Math.max(oldOffset, newOffset)
    });
    this.markWindowAsSkipped(oldOffset, newOffset);
    this._lastOffsetSeconds = newOffset;
  }
};

// src/managers/SessionManager.ts
import {
  RecoveryStrategy,
  ProgressPointer as ProgressPointer2
} from "@retreat/core";
import { createChildLogger as createChildLogger13 } from "@retreat/shared";

// src/types/version.ts
var DEFAULT_DAG_VERSION = "1.0.0";

// src/managers/SessionManager.ts
var SessionManager = class extends BaseManager {
  constructor(bus, coordinator, factory) {
    super();
    this.bus = bus;
    this.coordinator = coordinator;
    this.factory = factory;
  }
  bus;
  coordinator;
  factory;
  name = "SessionManager";
  priority = 10;
  // Initialize first
  log = createChildLogger13("SessionManager");
  currentSession = null;
  activeSession = null;
  _recoveryStrategy = RecoveryStrategy.WALL_CLOCK;
  get recoveryStrategy() {
    return this._recoveryStrategy;
  }
  setRecoveryStrategy(strategy) {
    if (strategy) this._recoveryStrategy = strategy;
  }
  async initialize() {
    this.log.flow("initialize: SessionManager ready");
    this.isReady_ = true;
  }
  configure(context) {
    if (context == null ? void 0 : context.session) {
      this.activeSession = context.session;
    }
  }
  /** INV-023: No active loops to suspend. */
  async stop() {
  }
  _doShutdown() {
    this.log.flow("shutdown: clearing session state");
    this.activeSession = null;
    this.currentSession = null;
  }
  reset() {
    this.log.flow("reset: clearing session state");
    this.currentSession = null;
    this.activeSession = null;
    this.isReady_ = false;
  }
  /**
   * Check if a session exists in persistence and validate it.
   * RIGHT BICEP: Boundary [B] — Returns plain data, no side effects.
   */
  async checkExistingSession(retreatId) {
    this.log.flow("checkExistingSession: validating persisted session", {
      retreatId
    });
    try {
      const session = await this.coordinator.loadSession(retreatId);
      if (!session) {
        return {
          exists: false,
          session: null,
          dagVersion: null,
          recoveryStrategy: null
        };
      }
      const expectedVersion = DEFAULT_DAG_VERSION;
      if (session.dagVersion !== expectedVersion) {
        this.log.flow("checkExistingSession: DAG version mismatch", {
          found: session.dagVersion,
          expected: expectedVersion
        });
        return {
          exists: false,
          session: null,
          dagVersion: session.dagVersion,
          recoveryStrategy: session.recoveryStrategy
        };
      }
      return {
        exists: true,
        session,
        dagVersion: session.dagVersion,
        recoveryStrategy: session.recoveryStrategy
      };
    } catch (error) {
      this.log.trace("checkExistingSession error", {
        error: error instanceof Error ? error.message : String(error)
      });
      return {
        exists: false,
        session: null,
        dagVersion: null,
        recoveryStrategy: null
      };
    }
  }
  restoreSession(state) {
    this.log.flow("restoreSession", {
      retreatId: state.retreatId,
      dagVersion: state.dagVersion
    });
    this.currentSession = state;
    this.activeSession = this.factory.createFromPersistedState(state);
    return this.activeSession;
  }
  createSession(schedule) {
    this.log.flow("createSession", { retreatId: schedule.id });
    this.activeSession = this.factory.create(schedule);
    this.currentSession = null;
    return this.activeSession;
  }
  /**
   * Get the active session (current playing session).
   */
  getActiveSession() {
    return this.activeSession;
  }
  /**
   * Get the persisted state (for recovery checks).
   */
  getCurrentSession() {
    return this.currentSession;
  }
  /**
   * Check if we have an active session ready to play.
   */
  hasActiveSession() {
    return this.activeSession !== null;
  }
  /**
   * Build persisted state from current session for persistence.
   * Takes timing data from HeartbeatManager getters and idempotency data.
   */
  buildPersistedState(opts) {
    this.log.flow("buildPersistedState", {
      offset: opts.lastOffset,
      totalPausedMs: opts.totalPausedMs
    });
    return {
      retreatId: opts.retreatId,
      absoluteStartTime: opts.absoluteStartTime,
      accumulatedPauseMs: opts.accumulatedPauseMs,
      totalPausedMs: opts.totalPausedMs,
      lastOffset: opts.lastOffset,
      pausedAt: opts.isPaused ? opts.pausedAt || Date.now() : null,
      savedAt: Date.now(),
      recoveryStrategy: opts.recoveryStrategy || RecoveryStrategy.WALL_CLOCK,
      dagVersion: opts.dagVersion || opts.retreatVersion || DEFAULT_DAG_VERSION,
      executedEventIds: opts.executedEventIds,
      updatedAt: Date.now()
    };
  }
  /**
   * Hydrate currentSession ISessionState from persisted state.
   * Returns the ISessionState for RetreatManager to store.
   */
  hydrateFromPersistedState(state) {
    this.log.flow("hydrateFromPersistedState", {
      retreatId: state.retreatId,
      offset: state.lastOffset
    });
    this.currentSession = state;
    return {
      retreatId: state.retreatId,
      clockOffsetSeconds: state.lastOffset,
      progressPointer: new ProgressPointer2("", 1, state.lastOffset)
    };
  }
};

// src/managers/TimelineManager.ts
import { createChildLogger as createChildLogger14 } from "@retreat/shared";
var TimelineManager = class extends BaseManager {
  constructor(bus) {
    super();
    this.bus = bus;
  }
  bus;
  name = "TimelineManager";
  priority = 15;
  log = createChildLogger14("TimelineManager");
  dagVersion = DEFAULT_DAG_VERSION;
  activeSession = null;
  async initialize() {
    this.log.flow("initialize: setting up timeline and DAG");
    if (!this.activeSession)
      throw new Error(
        "TimelineManager.initialize requires session via configure()"
      );
    if (!this.dagVersion) this.dagVersion = DEFAULT_DAG_VERSION;
    this.validateAndInjectExitNode();
  }
  configure(context) {
    if (context == null ? void 0 : context.session) {
      this.activeSession = context.session;
    }
  }
  /** INV-023: No active loops to suspend. */
  async stop() {
  }
  _doShutdown() {
    this.log.flow("shutdown: clearing timeline state");
    this.activeSession = null;
  }
  reset() {
    this.log.flow("reset: clearing timeline state");
    this.activeSession = null;
    this.dagVersion = DEFAULT_DAG_VERSION;
    this.isReady_ = false;
  }
  /**
   * Validate DAG exit node and inject synthetic exit if missing.
   * Called from selectRetreat to ensure DAG integrity.
   * Accepts optional session for delegation use.
   */
  validateAndInjectExitNode(session) {
    const targetSession = session || this.activeSession;
    if (!targetSession)
      throw new Error("No active session during exit node validation");
    const dag = targetSession.orchestrator.dag;
    if (!dag) {
      this.log.trace("validateAndInjectExitNode: no DAG, skipping");
      return;
    }
    const exitNodeExists = dag.exitNodeId && dag.nodes.find((n) => n.id === dag.exitNodeId);
    const exitHasOutgoing = !!dag.edges.find(
      (e) => e.fromNodeId === dag.exitNodeId
    );
    if (!exitNodeExists) {
      const syntheticExitId = `node:${targetSession.retreat.id}:exit:primary`;
      dag.nodes.push({
        id: syntheticExitId,
        type: NODE_TYPES.SILENCE,
        plane: PLANE_TYPES.PRIMARY,
        durationSeconds: 0,
        containerId: void 0
      });
      dag.exitNodeId = syntheticExitId;
      this.log.flow("validateAndInjectExitNode: added synthetic exit node", {
        nodeId: syntheticExitId
      });
    } else if (exitHasOutgoing) {
      this.log.flow(
        "validateAndInjectExitNode: exit node has outgoing edges (malformed DAG)",
        { exitNodeId: dag.exitNodeId }
      );
    }
  }
  async generateTimeline(resumeOffset) {
    if (!this.activeSession)
      throw new Error("No active session during timeline generation");
    const dag = this.activeSession.orchestrator.dag;
    if (!dag) throw new Error("DAG not available in orchestrator");
    this.log.flow("generateTimeline: generating from DAG");
    const events = await this.activeSession.orchestrator.timelineGenerator.generateAsync(
      dag
    );
    this.log.flow("generateTimeline", { eventsGenerated: events.length });
    this.activeSession.orchestrator.eventQueue.hydrate(events, resumeOffset);
    this.log.trace("generateTimeline: hydrate complete");
  }
  /**
   * Full initialize sequence: validate exit node, generate timeline, hydrate queue.
   * Extracted from RetreatManager.initialize() for proper delegation.
   */
  async generateAndHydrate(session, resumeOffset = 0) {
    var _a, _b;
    this.activeSession = session;
    const dag = session.orchestrator.dag;
    if (!dag) throw new Error("DAG not available in orchestrator");
    this.log.flow("generateAndHydrate", { resumeOffset });
    const events = await session.orchestrator.timelineGenerator.generateAsync(dag);
    this.log.flow("generateAndHydrate", { eventsGenerated: events.length });
    if (resumeOffset === 0 && ((_a = session.progress) == null ? void 0 : _a.offsetSeconds) > 0 && typeof process !== "undefined" && ((_b = process.env) == null ? void 0 : _b.NODE_ENV) === "development") {
      this.log.warn(
        `[DEV] generateAndHydrate: resumeOffset is 0 but session has progress. Did you forget to pass lastOffset to initialize()? Session offset: ${session.progress.offsetSeconds}s`
      );
    }
    session.orchestrator.eventQueue.hydrate(events, resumeOffset);
    this.log.trace("generateAndHydrate hydrate complete");
    this.isReady_ = true;
  }
  /**
   * Get current DAG version.
   */
  getDagVersion() {
    return this.dagVersion;
  }
  /**
   * Set DAG version during restore.
   */
  setDagVersion(version) {
    this.dagVersion = version;
  }
};

// src/managers/HeartbeatManager.ts
import { createChildLogger as createChildLogger15 } from "@retreat/shared";
var HeartbeatManager = class _HeartbeatManager extends BaseManager {
  constructor(getBus, clock, emitEvent, engineEvents) {
    super();
    this.getBus = getBus;
    this.clock = clock;
    this.emitEvent = emitEvent;
    this.engineEvents = engineEvents;
  }
  getBus;
  clock;
  emitEvent;
  engineEvents;
  name = "HeartbeatManager";
  priority = 20;
  log = createChildLogger15("HeartbeatManager");
  // The single anchor — set once, never recalculated
  _absoluteStartTime = 0;
  _totalPausedMs = 0;
  _pausedAt = null;
  _accumulatedPauseOffsetMs = 0;
  _isClockSubscribed = false;
  _rafId = null;
  _tickInterval = null;
  _timingStateRestored = false;
  static DRIFT_THRESHOLD_MS = 1e3;
  static TICK_INTERVAL_MS = 16;
  static MS_TO_SECONDS = 1e3;
  // Visibility/Focus listeners (Phase 2.2)
  _visibilityCleanup = null;
  _focusCleanup = null;
  /** INV-021: Epoch guard — set by RetreatManager after initialize(). */
  _epochGuard = () => 0;
  setEpochGuard(guard) {
    this._epochGuard = guard;
  }
  // --- Public Getters (for RetreatManager to read) ---
  get absoluteStartTime() {
    return this._absoluteStartTime;
  }
  get pausedAt() {
    return this._pausedAt;
  }
  get totalPausedMs() {
    return this._totalPausedMs;
  }
  get accumulatedPauseOffsetMs() {
    return this._accumulatedPauseOffsetMs;
  }
  get rafId() {
    return this._rafId;
  }
  /** Restore timing state from persisted session */
  restoreTimingState(absoluteStartTime, pausedAt, totalPausedMs, accumulatedPauseOffsetMs) {
    this._absoluteStartTime = absoluteStartTime;
    this._pausedAt = pausedAt;
    this._totalPausedMs = totalPausedMs;
    this._accumulatedPauseOffsetMs = accumulatedPauseOffsetMs;
    this._timingStateRestored = true;
    this.log.flow("restoreTimingState", {
      absoluteStartTime,
      pausedAt,
      totalPausedMs,
      accumulatedPauseOffsetMs
    });
  }
  // --- IManager lifecycle ---
  async initialize() {
    this._attachVisibilityListener();
    this._attachFocusListener();
    this.isReady_ = true;
    this.log.flow("initialize complete \u2014 visibility/focus listeners attached");
  }
  configure(context) {
  }
  /** INV-023: Stop the clock loop and clean up listeners. */
  async stop() {
    if (this._visibilityCleanup) {
      this._visibilityCleanup();
      this._visibilityCleanup = null;
    }
    if (this._focusCleanup) {
      this._focusCleanup();
      this._focusCleanup = null;
    }
    this.stopLoop();
    this.log.flow("stop: clock loop stopped");
  }
  _doShutdown() {
    this.stopLoop();
  }
  reset() {
    this._isClockSubscribed = false;
    this._rafId = null;
    this._tickInterval = null;
    this._absoluteStartTime = 0;
    this._totalPausedMs = 0;
    this._pausedAt = null;
    this._accumulatedPauseOffsetMs = 0;
    this._timingStateRestored = false;
    this.isReady_ = false;
  }
  /**
   * Start the clock loop.
   * On fresh start: sets absoluteStartTime anchor once.
   * On restored start: uses the existing anchor from restoreTimingState.
   * Returns offset in seconds (0 for fresh, restored offset otherwise).
   */
  startLoop(onTick) {
    const isFreshStart = !this._timingStateRestored && this._accumulatedPauseOffsetMs === 0;
    if (isFreshStart) {
      this._absoluteStartTime = Date.now();
      this._totalPausedMs = 0;
      this._pausedAt = null;
      this.log.flow("startLoop fresh", {
        absoluteStartTime: this._absoluteStartTime
      });
    } else {
      this.log.flow("startLoop resume from offset", {
        accumulatedPauseOffsetMs: this._accumulatedPauseOffsetMs,
        absoluteStartTime: this._absoluteStartTime,
        totalPausedMs: this._totalPausedMs
      });
    }
    this._subscribeToClock(onTick);
    const clockOffset = isFreshStart ? 0 : this._getCurrentOffsetMs() / _HeartbeatManager.MS_TO_SECONDS;
    this.clock.start(clockOffset);
    this._captureRafId();
    return isFreshStart ? 0 : clockOffset;
  }
  /**
   * Pause the clock loop.
   * Freezes offset using anchor math: (now - anchor - totalPaused).
   * Returns the frozen offset in seconds.
   */
  pauseLoop() {
    this._accumulatedPauseOffsetMs = this._absoluteStartTime > 0 ? Date.now() - this._absoluteStartTime - this._totalPausedMs : 0;
    this._pausedAt = Date.now();
    this.log.flow("pauseLoop froze offset", {
      offsetSeconds: this._accumulatedPauseOffsetMs / _HeartbeatManager.MS_TO_SECONDS,
      accumulatedPauseOffsetMs: this._accumulatedPauseOffsetMs
    });
    if (this.clock.pause) {
      this.clock.pause();
    }
    if (this._tickInterval) {
      clearInterval(this._tickInterval);
      this._tickInterval = null;
    }
    this._rafId = null;
    return this._accumulatedPauseOffsetMs / _HeartbeatManager.MS_TO_SECONDS;
  }
  /**
   * Resume from pause. Accumulates pause duration for drift detection.
   * Returns the offset in seconds for SYNC event.
   */
  resumeLoop(onTick) {
    if (this._pausedAt) {
      const pauseDuration = Date.now() - this._pausedAt;
      this._totalPausedMs += pauseDuration;
      this.log.flow("resumeLoop added pauseDuration", {
        pauseDuration,
        totalPausedMs: this._totalPausedMs
      });
    }
    const offsetSeconds = this._accumulatedPauseOffsetMs / _HeartbeatManager.MS_TO_SECONDS;
    this._pausedAt = null;
    this.log.flow("resumeLoop returning offset", {
      offsetSeconds,
      accumulatedPauseOffsetMs: this._accumulatedPauseOffsetMs
    });
    this._subscribeToClock(onTick);
    if (this.clock.start) {
      this.clock.start(offsetSeconds);
      this._captureRafId();
    } else {
      this._tickInterval = setInterval(() => {
        const elapsed = this._getCurrentOffsetMs() / _HeartbeatManager.MS_TO_SECONDS;
        onTick(Math.floor(elapsed));
      }, _HeartbeatManager.TICK_INTERVAL_MS);
    }
    return offsetSeconds;
  }
  /**
   * Full stop. Stops clock, clears all timing state.
   */
  stopLoop() {
    try {
      if (this.clock.stop) {
        this.clock.stop();
      }
    } catch (e) {
      this.log.trace("Error stopping clock", { error: e });
    }
    if (this._tickInterval) {
      clearInterval(this._tickInterval);
      this._tickInterval = null;
    }
    this._rafId = null;
    this._isClockSubscribed = false;
    this._accumulatedPauseOffsetMs = 0;
    this._pausedAt = null;
    this.log.flow("stopLoop complete - all timing reset");
  }
  /**
   * Current offset in seconds using anchor-only math.
   * Formula: (Date.now() - absoluteStartTime - totalPausedMs) / 1000
   */
  getCurrentOffsetSeconds(isPaused) {
    if (isPaused) {
      return this._accumulatedPauseOffsetMs / _HeartbeatManager.MS_TO_SECONDS;
    }
    if (this._absoluteStartTime > 0) {
      return this._getCurrentOffsetMs() / _HeartbeatManager.MS_TO_SECONDS;
    }
    return 0;
  }
  // --- Private helpers ---
  /** Anchor-only offset calculation in milliseconds */
  _getCurrentOffsetMs() {
    return Date.now() - this._absoluteStartTime - this._totalPausedMs;
  }
  _subscribeToClock(onTick) {
    if (this._isClockSubscribed) {
      this.log.trace("_subscribeToClock skipped \u2014 already subscribed");
      return;
    }
    this._isClockSubscribed = true;
    const capturedEpoch = this._epochGuard ? this._epochGuard() : 0;
    this.clock.onTick((elapsed) => {
      if (this._epochGuard && capturedEpoch !== this._epochGuard()) return;
      this._checkDrift(elapsed);
      onTick(elapsed);
      this._captureRafId();
    });
    this.log.flow("_subscribeToClock registered onTick callback");
  }
  /**
   * Check for drift between expected (wall-clock) and reported elapsed.
   * Emits SYNC if drift exceeds threshold.
   */
  _checkDrift(reportedElapsed) {
    const expectedElapsedMs = this._getCurrentOffsetMs();
    const reportedElapsedMs = reportedElapsed * _HeartbeatManager.MS_TO_SECONDS;
    const drift = Math.abs(expectedElapsedMs - reportedElapsedMs);
    if (drift > _HeartbeatManager.DRIFT_THRESHOLD_MS) {
      const newOffsetSeconds = Math.round(
        expectedElapsedMs / _HeartbeatManager.MS_TO_SECONDS
      );
      this.log.flow("_checkDrift: drift detected", {
        expectedElapsedMs,
        reportedElapsedMs,
        driftMs: drift,
        offsetSeconds: newOffsetSeconds
      });
      this._emitSync(newOffsetSeconds, drift);
    }
  }
  _emitSync(offsetSeconds, driftMs) {
    const payload = {
      offsetSeconds,
      absoluteStartTime: this._absoluteStartTime
    };
    if (driftMs !== void 0) {
      payload.driftMs = driftMs;
    }
    this.emitEvent(this.engineEvents.SYNC, payload);
    this.getBus().emit(this.engineEvents.SYNC, payload);
  }
  _captureRafId() {
    if (this.clock.rafId !== void 0) {
      this._rafId = this.clock.rafId;
    }
  }
  _attachVisibilityListener() {
    this._visibilityCleanup = this._attachDomListener(
      typeof document !== "undefined" ? document : null,
      "visibilitychange",
      () => typeof document !== "undefined" ? !document.hidden : false
    );
  }
  _attachFocusListener() {
    this._focusCleanup = this._attachDomListener(
      typeof window !== "undefined" ? window : null,
      "focus",
      () => true
    );
  }
  _attachDomListener(target, event, shouldHandle) {
    if (!target) {
      return () => {
      };
    }
    const capturedEpoch = this._epochGuard ? this._epochGuard() : 0;
    const handler = () => {
      if (this._epochGuard && capturedEpoch !== this._epochGuard()) return;
      if (shouldHandle()) this._reconcile();
    };
    target.addEventListener(event, handler);
    return () => target.removeEventListener(event, handler);
  }
  /**
   * Reconcile elapsed time against wall-clock.
   * Called on visibility/focus changes.
   * Emits SYNC if drift exceeds threshold.
   *
   * GUARD: Only reconcile if engine is PAUSED (RAF is paused).
   * If RAF is running, ticks will naturally handle sync. Reconciling
   * during RUNNING state caused massive drift (wall-clock - pauseOffset).
   */
  _reconcile() {
    if (this._absoluteStartTime === 0) {
      this.log.trace("_reconcile skipped \u2014 retreat not started (no anchor)");
      return;
    }
    if (this._rafId !== null) {
      this.log.trace("_reconcile skipped \u2014 RAF is running");
      return;
    }
    const wallClockElapsedMs = this._getCurrentOffsetMs();
    const engineElapsedMs = this._accumulatedPauseOffsetMs;
    const drift = Math.abs(wallClockElapsedMs - engineElapsedMs);
    if (drift > _HeartbeatManager.DRIFT_THRESHOLD_MS) {
      const newOffsetSeconds = Math.round(
        wallClockElapsedMs / _HeartbeatManager.MS_TO_SECONDS
      );
      this.log.flow("_reconcile: drift detected", {
        wallClockElapsedMs,
        engineElapsedMs,
        driftMs: drift,
        newOffsetSeconds
      });
      this._emitSync(newOffsetSeconds, drift);
    }
  }
};

// src/managers/CommandManager.ts
import { createManualEvent } from "@retreat/core";
import { createChildLogger as createChildLogger16 } from "@retreat/shared";
var CommandManager = class extends BaseManager {
  constructor(bus) {
    super();
    this.bus = bus;
  }
  bus;
  name = "CommandManager";
  priority = 25;
  log = createChildLogger16("CommandManager");
  activeSession = null;
  async initialize() {
    this.log.flow("initialize: setting up command pipeline");
    if (!this.activeSession) {
      throw new Error(
        "CommandManager.initialize requires session via configure()"
      );
    }
    this.isReady_ = true;
  }
  configure(context) {
    if (context == null ? void 0 : context.session) {
      this.activeSession = context.session;
    }
  }
  /** INV-023: No active loops to suspend. */
  async stop() {
  }
  _doShutdown() {
    this.log.flow("shutdown: clearing command state");
    this.activeSession = null;
  }
  reset() {
    this.log.flow("reset: clearing command state");
    this.activeSession = null;
    this.isReady_ = false;
  }
  /**
   * Inject an event into the timeline.
   * Event will be processed by orchestrator.
   */
  inject(event) {
    this.log.flow("inject", { eventId: event.id });
    if (!this.activeSession) {
      throw new Error("No active session during inject");
    }
    this.activeSession.orchestrator.inject(event);
  }
  /**
   * Inject a manual event (created at current offset).
   * Useful for user-triggered or programmatic events.
   */
  injectManualEvent(type, payload) {
    var _a;
    this.log.flow("injectManualEvent", { type });
    const manualEvent = createManualEvent(
      PLANE_TYPES.OVERLAY,
      { ...payload, type },
      ((_a = this.activeSession) == null ? void 0 : _a.progress.offsetSeconds) || 0
    );
    this.inject(manualEvent);
  }
  /**
   * Bulk inject events into the orchestrator and flush.
   */
  bulkInject(events) {
    this.log.flow("bulkInject", { count: events.length });
    if (!this.activeSession) {
      throw new Error("No active session during bulkInject");
    }
    this.activeSession.orchestrator.inject(events, false);
    this.activeSession.orchestrator.flush();
  }
  /**
   * Flush the orchestrator's pending events.
   */
  flush() {
    this.log.flow("flush");
    if (!this.activeSession) {
      throw new Error("No active session during flush");
    }
    this.activeSession.orchestrator.flush();
  }
  /**
   * Dispatch a command against the given context.
   */
  dispatch(command, context) {
    this.log.flow("dispatch command");
    command.execute(context);
  }
  /**
   * Set the active session for command operations.
   * Called by RetreatManager during selectRetreat.
   */
  setActiveSession(session) {
    var _a;
    this.activeSession = session;
    this.isReady_ = true;
    this.log.flow("setActiveSession", { retreatId: (_a = session == null ? void 0 : session.retreat) == null ? void 0 : _a.id });
  }
};

// src/managers/RecoveryManager.ts
import { createChildLogger as createChildLogger17 } from "@retreat/shared";
var RecoveryManager = class extends BaseManager {
  constructor(bus) {
    super();
    this.bus = bus;
  }
  bus;
  name = "RecoveryManager";
  priority = 30;
  log = createChildLogger17("RecoveryManager");
  activeSession = null;
  planes = [];
  clock = null;
  persistenceModule = null;
  async initialize() {
    this.log.flow("initialize: setting up recovery and sync");
    if (!this.activeSession || !this.planes || !this.clock || !this.persistenceModule) {
      throw new Error(
        "RecoveryManager.initialize requires session, planes, clock, persistenceModule via configure()"
      );
    }
    this.isReady_ = true;
  }
  configure(context) {
    if (context == null ? void 0 : context.session) this.activeSession = context.session;
    if (context == null ? void 0 : context.planes) this.planes = context.planes;
    if (context == null ? void 0 : context.clock) this.clock = context.clock;
    if (context == null ? void 0 : context.persistenceManager)
      this.persistenceModule = context.persistenceManager;
    if (context == null ? void 0 : context.persistenceModule)
      this.persistenceModule = context.persistenceModule;
  }
  /** INV-023: No active loops to suspend. */
  async stop() {
  }
  _doShutdown() {
    this.log.flow("shutdown: clearing recovery state");
    this.activeSession = null;
    this.planes = [];
    this.clock = null;
    this.persistenceModule = null;
  }
  reset() {
    this.log.flow("reset: clearing recovery state");
    this.activeSession = null;
    this.planes = [];
    this.clock = null;
    this.persistenceModule = null;
    this.isReady_ = false;
  }
  /**
   * Sync/teleport to arbitrary offset with full plane and clock synchronization.
   * Planes are synced to render immediate state at target offset.
   * Useful for user seeking or recovery jumps.
   * Emits SYNC event for UI to snap to new offset.
   */
  sync(offsetSeconds, emitSyncEvent) {
    var _a;
    this.log.flow("sync", { offsetSeconds });
    if (!this.activeSession || !this.clock) {
      throw new Error("No active session or clock during sync");
    }
    this._seekCore(offsetSeconds);
    for (const plane of this.planes) {
      try {
        (_a = plane.sync) == null ? void 0 : _a.call(plane, offsetSeconds);
      } catch (e) {
        this.log.trace("Error during plane.sync", {
          planeName: plane.constructor.name,
          error: e
        });
      }
    }
    try {
      if (this.clock.start) {
        this.clock.start(offsetSeconds);
      }
    } catch (e) {
      this.log.trace("Error calling clock.start", {
        offsetSeconds,
        error: e
      });
    }
    emitSyncEvent({ offsetSeconds });
    this.log.trace("sync complete", { offsetSeconds });
  }
  /**
   * Seek to arbitrary offset - similar to sync but for mid-execution seeking.
   * Used when user drags timeline scrubber or seeks programmatically.
   * Does NOT emit events (RetreatManager handles events).
   */
  seek(offsetSeconds) {
    this.log.flow("seek", { offsetSeconds });
    if (!this.activeSession) {
      this.log.trace("No active session for seek, ignoring");
      return;
    }
    this._seekCore(offsetSeconds);
    this.log.flow("seek complete", { offsetSeconds });
  }
  /**
   * Core seek logic: stop planes, rehydrate queue, update progress.
   * @private
   */
  _seekCore(offsetSeconds) {
    var _a;
    for (const plane of this.planes) {
      (_a = plane.stopAll) == null ? void 0 : _a.call(plane);
    }
    this.activeSession.orchestrator.rehydrate(offsetSeconds);
    this.activeSession.progress.offsetSeconds = offsetSeconds;
  }
  /**
   * Persist current session state to storage.
   * Called during pause and recovery points.
   */
  async persistState(state, tier) {
    if (!this.persistenceModule) {
      throw new Error("No persistence module available");
    }
    this.log.flow("persistState", { offset: state.lastOffset, tier });
    try {
      await this.persistenceModule.saveSession(state, tier);
      this.log.trace("persistState complete", { offset: state.lastOffset });
    } catch (err) {
      this.log.trace("Failed to persist session state", { error: err });
    }
  }
  handleRecovery(result, syncFn) {
    this.log.flow("handleRecovery", { action: result.action });
    if (result.action === RECOVERY_ACTIONS.NONE) {
      this.log.trace("No recovery needed");
      return;
    }
    if (result.action === RECOVERY_ACTIONS.SYNC) {
      this.log.info("Syncing to real-world time", { deltaSec: result.delta });
      syncFn(result.delta);
      return;
    }
    if (result.action === RECOVERY_ACTIONS.RESUME) {
      this.log.info("Resuming from pause point", {
        offsetSec: result.lastOffset
      });
      syncFn(result.lastOffset);
    }
  }
};

// src/managers/SystemAlertManager.ts
import { createChildLogger as createChildLogger18 } from "@retreat/shared";
var SystemAlertManager = class extends BaseManager {
  name = "SystemAlertManager";
  priority = 35;
  log = createChildLogger18("SystemAlertManager");
  _silent;
  // Resolved at construction time (not lazily) so warmUp() works from user gesture
  // handlers before initialize() is called.
  _synth;
  constructor(options = {}) {
    super();
    this._silent = options.silent ?? false;
    this._synth = !this._silent && typeof window !== "undefined" && "speechSynthesis" in window ? window.speechSynthesis : null;
  }
  _queue = [];
  _speaking = false;
  _cancelling = false;
  _userDisabledTTS = false;
  _firstEnqueue = true;
  // Held to prevent Chrome GC from collecting the utterance before speech finishes.
  _currentUtterance = null;
  // Maps dedupKey → timestamp of last delivery. Supports both forever-dedup and time-windowed dedup.
  _dedupTimestamps = /* @__PURE__ */ new Map();
  enqueue(alert) {
    if (!this.isReady_ || !this._synth || this._userDisabledTTS) {
      this.log.warn("enqueue blocked", {
        text: alert.text,
        isReady: this.isReady_,
        hasSynth: !!this._synth,
        userDisabledTTS: this._userDisabledTTS
      });
      return;
    }
    if (this._firstEnqueue) {
      this._firstEnqueue = false;
    }
    this.log.info("enqueue", { text: alert.text, dedupKey: alert.dedupKey ?? null });
    if (alert.dedupKey) {
      const lastFired = this._dedupTimestamps.get(alert.dedupKey) ?? 0;
      const window2 = alert.dedupWindowMs ?? Infinity;
      if (Date.now() - lastFired < window2) {
        this.log.warn("enqueue suppressed by dedup", { dedupKey: alert.dedupKey, text: alert.text });
        return;
      }
      this._dedupTimestamps.set(alert.dedupKey, Date.now());
    }
    if (alert.severity === "critical") {
      this._cancelling = true;
      this._synth.cancel();
      this._cancelling = false;
      this._speaking = false;
      this._queue.unshift(alert);
    } else {
      this._queue.push(alert);
    }
    this._drain();
  }
  _drain() {
    if (this._cancelling || this._speaking || !this._synth || !this._queue.length) {
      if (this._queue.length && (this._cancelling || this._speaking)) {
        this.log.info("_drain deferred", {
          cancelling: this._cancelling,
          speaking: this._speaking,
          queued: this._queue.length
        });
      }
      return;
    }
    const a = this._queue.shift();
    const u = new SpeechSynthesisUtterance(a.text);
    u.rate = 0.9;
    u.volume = 0.8;
    u.onend = () => {
      this._currentUtterance = null;
      this._speaking = false;
      this._drain();
    };
    u.onerror = (e) => {
      if (this._cancelling) return;
      this.log.warn("utterance error", { text: a.text, error: (e == null ? void 0 : e.error) ?? "unknown" });
      this._currentUtterance = null;
      this._speaking = false;
      this._drain();
    };
    this._speaking = true;
    this._currentUtterance = u;
    this._synth.resume();
    this.log.info("speechSynthesis.speak() called", {
      text: a.text,
      synthPaused: this._synth.paused,
      synthSpeaking: this._synth.speaking,
      synthPending: this._synth.pending
    });
    this._synth.speak(u);
  }
  configure() {
  }
  async initialize() {
    this.isReady_ = true;
    this.log.info("initialize complete", {
      hasSpeechSynthesis: !!this._synth,
      isSilent: this._silent,
      userDisabledTTS: this._userDisabledTTS
    });
  }
  /**
   * iOS Safari requires speech synthesis to be invoked from a user-gesture handler.
   * Call this synchronously (before any await) from gesture handlers (e.g. intention
   * submit, "Begin retreat") to unlock timer-based speak() calls for the session.
   * Safe to call before initialize() — only requires _synth, not isReady_.
   */
  warmUp() {
    if (!this._synth || this._userDisabledTTS) return;
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    u.rate = 10;
    u.onend = () => {
    };
    u.onerror = () => {
    };
    this._synth.speak(u);
  }
  setEnabled(enabled) {
    this._userDisabledTTS = !enabled;
    this.log.info("TTS enabled changed", { enabled });
  }
  async stop() {
    var _a;
    this._cancelling = true;
    (_a = this._synth) == null ? void 0 : _a.cancel();
    this._cancelling = false;
    this._queue = [];
    this._speaking = false;
  }
  async _doShutdown() {
    await this.stop();
    this._dedupTimestamps.clear();
  }
  reset() {
    var _a;
    this._cancelling = true;
    (_a = this._synth) == null ? void 0 : _a.cancel();
    this._cancelling = false;
    this._queue = [];
    this._dedupTimestamps.clear();
    this._speaking = false;
    this._firstEnqueue = true;
  }
};

// src/managers/ResilienceManager.ts
var TICK_GAP_THRESHOLD_MS = 5e3;
var MONITOR_INTERVAL_MS = 2e3;
var ResilienceManager = class extends BaseManager {
  constructor(bus) {
    super();
    this.bus = bus;
  }
  bus;
  name = "ResilienceManager";
  priority = 28;
  // below RecoveryManager (30), above HeartbeatManager (20)
  _lastTickMs = 0;
  _monitorId = null;
  eventSubscriptions = {
    [ENGINE_EVENTS.TICK]: () => {
      this._lastTickMs = Date.now();
    },
    [ENGINE_EVENTS.EXECUTION_ERROR]: (data) => {
      var _a, _b;
      this.bus.emit(ENGINE_EVENTS.RESILIENCE_WARNING, {
        type: "execution_error",
        message: `Execution error for event ${((_a = data.event) == null ? void 0 : _a.id) ?? "unknown"}: ${((_b = data.error) == null ? void 0 : _b.message) ?? "unknown"}`
      });
    }
  };
  configure() {
  }
  async initialize() {
    this._lastTickMs = Date.now();
    this.isReady_ = true;
  }
  async stop() {
    this._clearMonitor();
  }
  reset() {
    this._lastTickMs = 0;
  }
  startMonitoring() {
    this._clearMonitor();
    this._monitorId = setInterval(() => {
      if (!this.isReady_) return;
      const msSinceLastTick = Date.now() - this._lastTickMs;
      if (msSinceLastTick > TICK_GAP_THRESHOLD_MS) {
        this.bus.emit(ENGINE_EVENTS.RESILIENCE_WARNING, {
          type: "tick_gap",
          message: `No tick in ${msSinceLastTick}ms \u2014 engine may be stalled`
        });
      }
    }, MONITOR_INTERVAL_MS);
  }
  _doShutdown() {
    this._clearMonitor();
  }
  _clearMonitor() {
    if (this._monitorId !== null) {
      clearInterval(this._monitorId);
      this._monitorId = null;
    }
  }
};

// src/managers/ConnectivityManager.ts
import { createChildLogger as createChildLogger19 } from "@retreat/shared";
var DEBOUNCE_MS = 500;
var ConnectivityManager = class extends BaseManager {
  constructor(bus) {
    super();
    this.bus = bus;
    this._isOnline = typeof navigator !== "undefined" ? navigator.onLine ?? true : true;
  }
  bus;
  name = "ConnectivityManager";
  priority = 40;
  log = createChildLogger19("ConnectivityManager");
  _debounceTimer = null;
  _isOnline;
  configure() {
  }
  async initialize() {
    if (typeof window !== "undefined") {
      window.addEventListener("online", this._onOnline);
      window.addEventListener("offline", this._onOffline);
    }
    this.log.trace("ConnectivityManager started", { online: this._isOnline });
    this.isReady_ = true;
  }
  async stop() {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }
  _doShutdown() {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this._onOnline);
      window.removeEventListener("offline", this._onOffline);
    }
    this.log.trace("ConnectivityManager shutdown");
  }
  reset() {
    this._isOnline = typeof navigator !== "undefined" ? navigator.onLine ?? true : true;
  }
  isOnline() {
    return this._isOnline;
  }
  _onOnline = () => {
    this._scheduleEmit(true);
  };
  _onOffline = () => {
    this._scheduleEmit(false);
  };
  _scheduleEmit(online) {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
    }
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      if (online === this._isOnline) return;
      this._isOnline = online;
      if (online) {
        this.bus.emit(ENGINE_EVENTS.CONNECTIVITY_ONLINE);
        this.log.flow("connectivity restored");
      } else {
        this.bus.emit(ENGINE_EVENTS.CONNECTIVITY_OFFLINE);
        this.log.flow("connectivity lost");
      }
    }, DEBOUNCE_MS);
  }
};

// src/managers/SessionCheckpointManager.ts
import { createChildLogger as createChildLogger20 } from "@retreat/shared";
function djb2(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}
function computeChecksum(state) {
  return djb2(JSON.stringify(state));
}
function slotKey(retreatId, slot) {
  return `rc:ckpt:v1:${retreatId}:${slot}`;
}
function headKey(retreatId) {
  return `rc:ckpt:v1:${retreatId}:head`;
}
function makeLocalStorageAdapter() {
  try {
    if (typeof localStorage === "undefined") return null;
    const ls = localStorage;
    return {
      getItem: (k) => ls.getItem(k),
      setItem: (k, v) => {
        ls.setItem(k, v);
      }
    };
  } catch {
    return null;
  }
}
var SessionCheckpointManager = class extends BaseManager {
  name = "SessionCheckpointManager";
  priority = 2;
  log = createChildLogger20("SessionCheckpointManager");
  _buildStateFn = null;
  _storage;
  eventSubscriptions = {
    [ENGINE_EVENTS.EXECUTION_SUCCESS]: () => {
      this._checkpoint();
    }
  };
  constructor(storage) {
    super();
    this._storage = storage ?? makeLocalStorageAdapter();
  }
  configure(context) {
    if (context == null ? void 0 : context.buildStateFn) {
      this._buildStateFn = context.buildStateFn;
    }
  }
  async initialize() {
    this.isReady_ = true;
  }
  async stop() {
  }
  _doShutdown() {
    this._buildStateFn = null;
  }
  reset() {
    this._buildStateFn = null;
    this.isReady_ = false;
  }
  /**
   * Remove all checkpoint data for a retreat (both slots + head pointer).
   * Call this when a session is explicitly stopped/cleared so that a future
   * probe() does not offer a stale resume prompt.
   */
  clearCheckpoint(retreatId) {
    if (!this._storage) return;
    try {
      this._storage.setItem(slotKey(retreatId, 0), "");
      this._storage.setItem(slotKey(retreatId, 1), "");
      this._storage.setItem(headKey(retreatId), "");
    } catch (err) {
      this.log.flow("clearCheckpoint failed", {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }
  /**
   * Attempt to load the best valid persisted state for a retreat.
   * Tries the most-recently-written slot first, then falls back to the other.
   * Returns null if both are absent or checksum-invalid.
   */
  loadValidState(retreatId) {
    if (!this._storage) return null;
    const head = this._readHead(retreatId);
    const alt = head === 0 ? 1 : 0;
    return this._trySlot(retreatId, head) ?? this._trySlot(retreatId, alt);
  }
  _checkpoint() {
    if (!this._buildStateFn || !this._storage) return;
    try {
      const state = this._buildStateFn();
      if (!(state == null ? void 0 : state.retreatId)) return;
      const cs = computeChecksum(state);
      const head = this._readHead(state.retreatId);
      const nextSlot = head === 0 ? 1 : 0;
      const data = {
        state,
        checksum: cs,
        savedAt: Date.now(),
        slot: nextSlot
      };
      this._storage.setItem(
        slotKey(state.retreatId, nextSlot),
        JSON.stringify(data)
      );
      this._storage.setItem(headKey(state.retreatId), String(nextSlot));
      this.log.trace("checkpoint saved", {
        retreatId: state.retreatId,
        slot: nextSlot,
        offset: state.lastOffset
      });
    } catch (err) {
      this.log.flow("checkpoint save failed", {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }
  _readHead(retreatId) {
    if (!this._storage) return 0;
    const raw = this._storage.getItem(headKey(retreatId));
    return raw === "1" ? 1 : 0;
  }
  _trySlot(retreatId, slot) {
    if (!this._storage) return null;
    try {
      const raw = this._storage.getItem(slotKey(retreatId, slot));
      if (!raw) return null;
      const data = JSON.parse(raw);
      const expected = computeChecksum(data.state);
      if (data.checksum !== expected) {
        this.log.flow("checkpoint corrupted \u2014 checksum mismatch", {
          retreatId,
          slot
        });
        return null;
      }
      return data.state;
    } catch {
      this.log.flow("checkpoint load failed \u2014 parse error", {
        retreatId,
        slot
      });
      return null;
    }
  }
};

// src/managers/IManagerFactory.ts
var DefaultManagerFactory = class {
  createPersistenceManager(deps) {
    return new PersistenceManager(deps.bus, deps.coordinator);
  }
  createIdempotencyManager(deps) {
    return new IdempotencyManager(deps.bus);
  }
  createSessionManager(deps) {
    return new SessionManager(deps.bus, deps.coordinator, deps.sessionFactory);
  }
  createTimelineManager(deps) {
    return new TimelineManager(deps.bus);
  }
  createHeartbeatManager(deps) {
    return new HeartbeatManager(
      () => deps.bus,
      deps.clock,
      deps.emitEvent,
      deps.engineEvents
    );
  }
  createCommandManager(deps) {
    return new CommandManager(deps.bus);
  }
  createRecoveryManager(deps) {
    return new RecoveryManager(deps.bus);
  }
  createSystemAlertManager(deps) {
    return new SystemAlertManager({ silent: deps.silent });
  }
  createResilienceManager(deps) {
    return new ResilienceManager(deps.bus);
  }
  createConnectivityManager(deps) {
    return new ConnectivityManager(deps.bus);
  }
  createCheckpointManager(_deps) {
    return new SessionCheckpointManager();
  }
};

// src/machine/scheduler-machine.ts
import { setup, assign } from "xstate";

// src/machine/actors/timer-actor.ts
import { fromCallback } from "xstate";
var timerActor = fromCallback(
  ({
    sendBack,
    receive,
    input
  }) => {
    const hb = input == null ? void 0 : input.heartbeatManager;
    if (!hb) {
      return () => {
      };
    }
    const offset = hb.startLoop((elapsed) => {
      sendBack({ type: "TICK", elapsed });
    });
    if (offset > 0) {
      sendBack({ type: "SYNC", offsetSeconds: offset });
    }
    receive((event) => {
      if (event.type === "PAUSE") {
        hb.pauseLoop();
      }
    });
    return () => {
      hb.stopLoop();
    };
  }
);

// src/machine/scheduler-machine.ts
function createSchedulerMachine() {
  return setup({
    types: {},
    actors: {
      timerActor
    },
    guards: {
      hasSchedule: ({ context }) => context.schedule !== null,
      hasPending: ({ event }) => {
        if (event.type === "PROBE_DONE") {
          return event.session !== null;
        }
        return false;
      },
      noPending: ({ event }) => {
        if (event.type === "PROBE_DONE") {
          return event.session === null;
        }
        return false;
      }
    }
  }).createMachine({
    id: "scheduler",
    initial: "idle",
    context: ({ input }) => ({
      schedule: null,
      pendingSession: null,
      elapsedSeconds: 0,
      error: null,
      heartbeatManager: (input == null ? void 0 : input.heartbeatManager) ?? null,
      completionType: null
    }),
    states: {
      idle: {
        on: {
          SELECT_RETREAT: {
            target: "probing",
            actions: assign({ schedule: ({ event }) => event.schedule })
          },
          INIT_READY: "initializing",
          INIT_DONE: "initializing",
          START: "initializing",
          RESTORE_PAUSED: "paused",
          ERROR: {
            target: "error",
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      probing: {
        on: {
          PROBE_DONE: [
            {
              guard: "hasPending",
              target: "resuming",
              actions: assign({
                pendingSession: ({ event }) => event.session
              })
            },
            {
              guard: "noPending",
              target: "initializing"
            }
          ],
          ERROR: {
            target: "error",
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      resuming: {
        on: {
          CONFIRM_RESUME: "initializing",
          START_FRESH: {
            target: "initializing",
            actions: assign({ pendingSession: null })
          }
        }
      },
      initializing: {
        on: {
          INIT_DONE: "running",
          START: "running",
          STOP: {
            target: "idle",
            actions: assign({ completionType: null })
          },
          RESTORE_PAUSED: "paused",
          ERROR: {
            target: "error",
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      running: {
        on: {
          TICK: {
            actions: assign({
              elapsedSeconds: ({ event }) => event.elapsed
            })
          },
          PAUSE: "paused",
          STOP: {
            target: "completing",
            actions: assign({
              completionType: "stopped"
            })
          },
          COMPLETE_SCHEDULE: {
            target: "completing",
            actions: assign({
              completionType: "completed"
            })
          }
        }
      },
      paused: {
        on: {
          RESUME: "running",
          STOP: {
            target: "completing",
            actions: assign({ completionType: "stopped" })
          }
        }
      },
      completing: {
        on: {
          CLEANUP_DONE: {
            target: "idle",
            actions: assign({
              elapsedSeconds: 0,
              pendingSession: null,
              completionType: null
            })
          }
        }
      },
      error: {
        on: {
          RETRY: {
            target: "idle",
            actions: assign({ error: null })
          }
        }
      }
    }
  });
}

// src/controllers/WakeLockController.ts
var WakeLockController = class {
  constructor(_onRunning) {
    this._onRunning = _onRunning;
  }
  _onRunning;
  _sentinel = null;
  _visibilityHandler = null;
  /**
   * Acquire the wake lock. No-op if API unavailable or already held.
   */
  async acquire() {
    if (!this._isSupported()) return;
    if (this._sentinel) return;
    try {
      this._sentinel = await navigator.wakeLock.request("screen");
      this._sentinel.addEventListener("release", () => {
        this._sentinel = null;
      });
      this._attachVisibilityListener();
    } catch {
    }
  }
  /**
   * Release the wake lock and detach the visibility listener.
   */
  async release() {
    this._detachVisibilityListener();
    if (this._sentinel) {
      try {
        await this._sentinel.release();
      } catch {
      }
      this._sentinel = null;
    }
  }
  get isHeld() {
    return this._sentinel !== null;
  }
  _isSupported() {
    return typeof navigator !== "undefined" && "wakeLock" in navigator;
  }
  _attachVisibilityListener() {
    if (typeof document === "undefined") return;
    if (this._visibilityHandler) return;
    this._visibilityHandler = () => {
      if (document.visibilityState === "visible" && !this._sentinel) {
        if (!this._onRunning || this._onRunning()) {
          void this.acquire();
        }
      }
    };
    document.addEventListener("visibilitychange", this._visibilityHandler);
  }
  _detachVisibilityListener() {
    if (this._visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }
};

// src/controllers/BatteryMonitor.ts
import { createChildLogger as createChildLogger21 } from "@retreat/shared";
var THRESHOLDS = { LOW_WARNING: 0.2, LOW_CRITICAL: 0.1 };
var BatteryMonitor = class {
  log = createChildLogger21("BatteryMonitor");
  _battery = null;
  _subs = [];
  _lastFiredThreshold = null;
  async start() {
    if (typeof navigator === "undefined" || !("getBattery" in navigator))
      return;
    try {
      this._battery = await navigator.getBattery();
      this._battery.addEventListener("levelchange", this._onLevelChange);
      this._battery.addEventListener("chargingchange", this._onChargingChange);
      this.log.trace("BatteryMonitor started", { level: this._battery.level });
    } catch (err) {
      this.log.flow("BatteryMonitor.start() failed \u2014 Battery API unavailable", {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }
  getLevel() {
    var _a;
    return ((_a = this._battery) == null ? void 0 : _a.level) ?? null;
  }
  isCharging() {
    var _a;
    return ((_a = this._battery) == null ? void 0 : _a.charging) ?? false;
  }
  onThresholdCrossed(cb) {
    this._subs.push(cb);
    return () => {
      this._subs = this._subs.filter((s) => s !== cb);
    };
  }
  dispose() {
    var _a, _b;
    (_a = this._battery) == null ? void 0 : _a.removeEventListener("levelchange", this._onLevelChange);
    (_b = this._battery) == null ? void 0 : _b.removeEventListener(
      "chargingchange",
      this._onChargingChange
    );
    this._battery = null;
    this._subs = [];
  }
  _onLevelChange = () => {
    var _a;
    const level = ((_a = this._battery) == null ? void 0 : _a.level) ?? 1;
    this.log.trace("battery levelchange", {
      level,
      lastFired: this._lastFiredThreshold
    });
    if (level <= THRESHOLDS.LOW_CRITICAL && this._lastFiredThreshold !== "LOW_CRITICAL") {
      this._lastFiredThreshold = "LOW_CRITICAL";
      this._emit({
        threshold: "LOW_CRITICAL",
        level,
        isCharging: this.isCharging()
      });
    } else if (level <= THRESHOLDS.LOW_WARNING && level > THRESHOLDS.LOW_CRITICAL && this._lastFiredThreshold !== "LOW_WARNING" && this._lastFiredThreshold !== "LOW_CRITICAL") {
      this._lastFiredThreshold = "LOW_WARNING";
      this._emit({
        threshold: "LOW_WARNING",
        level,
        isCharging: this.isCharging()
      });
    }
  };
  _onChargingChange = () => {
    var _a;
    if ((_a = this._battery) == null ? void 0 : _a.charging) {
      this._lastFiredThreshold = null;
      this._emit({
        threshold: "CHARGING_RESUMED",
        level: this._battery.level,
        isCharging: true
      });
    }
  };
  _emit(e) {
    this._subs.forEach((cb) => cb(e));
  }
};

// src/controllers/StorageQuotaMonitor.ts
import { createChildLogger as createChildLogger22 } from "@retreat/shared";
var StorageQuotaMonitor = class {
  log = createChildLogger22("StorageQuotaMonitor");
  intervalId = null;
  warned = false;
  subs = [];
  async start() {
    if (typeof navigator === "undefined" || !navigator.storage || !navigator.storage.estimate) {
      this.log.trace("Storage API not available");
      return;
    }
    await this.check();
    this.intervalId = setInterval(() => this.check(), 5 * 60 * 1e3);
  }
  onThresholdCrossed(cb) {
    this.subs.push(cb);
    return () => {
      this.subs = this.subs.filter((s) => s !== cb);
    };
  }
  async check() {
    try {
      const { quota, usage } = await navigator.storage.estimate();
      if (quota && usage) {
        const usagePercent = usage / quota;
        if (usagePercent >= 0.9 && !this.warned) {
          this.warned = true;
          this.subs.forEach((cb) => cb(usagePercent));
        } else if (usagePercent < 0.9) {
          this.warned = false;
        }
      }
    } catch (e) {
      this.log.trace("Storage estimate failed", { error: e });
    }
  }
  dispose() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.subs = [];
  }
};

// src/RetreatManager.ts
var DRIFT_VOICE_THRESHOLD_MS = 5e3;
var DRIFT_ALERT_COOLDOWN_MS = 6e4;
var BATTERY_ALERT_MAP = {
  LOW_WARNING: {
    text: "Battery low, please plug in",
    severity: "warning",
    dedupKey: "battery-low"
  },
  LOW_CRITICAL: {
    text: "Battery critical",
    severity: "critical",
    dedupKey: "battery-critical"
  },
  CHARGING_RESUMED: {
    text: "Charging resumed",
    severity: "info",
    dedupKey: void 0
  }
};
function generateInstanceId() {
  return Math.random().toString(36).substring(2, 11);
}
var RetreatManager = class _RetreatManager extends TypedEmitter {
  // ─── Private constructor (side-effect-free) ───────────────────────────────
  constructor(clock, coordinator, lifecycleModule, instanceId, scope, managerFactory, _silent = false) {
    super();
    this.clock = clock;
    this.coordinator = coordinator;
    this._silent = _silent;
    this.instanceId = instanceId;
    this._scope = scope;
    this.lifecycleModule = lifecycleModule;
    this.factory = new RetreatSessionFactory(clock);
    this._managerFactory = managerFactory ?? new DefaultManagerFactory();
    this.log = createChildLogger23("RetreatManager", { instanceId });
    this.log.info("runtime.lock.acquired", { instanceId, scope });
    this.log.info("runtime.constructed", { instanceId });
  }
  clock;
  coordinator;
  _silent;
  // ─── Module singleton (Phase 1) ──────────────────────────────────────────
  static _instance = null;
  /**
   * Module-level singleton factory. Returns the same instance on every call.
   * The instance is constructed at module scope by initializeManager() at app boot.
   *
   * Usage:
   *   const manager = RetreatManager.get();
   */
  static get() {
    if (!this._instance) {
      throw new Error(
        "[RetreatManager] Singleton not initialized. Call initializeManager() at app boot."
      );
    }
    return this._instance;
  }
  /**
   * Initialize the module singleton. Called once at app boot from main.tsx.
   * Constructs clock and coordinator at module scope.
   */
  static initialize(clock, coordinator, silent = false) {
    if (this._instance) {
      throw new Error("[RetreatManager] Singleton already initialized");
    }
    const instanceId = `singleton_${generateInstanceId()}`;
    this._instance = new _RetreatManager(
      clock,
      coordinator,
      null,
      instanceId,
      "default",
      void 0,
      silent
    );
    this._instance._setup();
  }
  /**
   * Reset the singleton (for testing only). Does not call destroy() to avoid
   * side effects — just nulls the instance reference.
   */
  static async _resetForTesting() {
    if (this._instance) {
      try {
        await this._instance.destroy();
      } catch (err) {
      }
      this._instance = null;
    }
  }
  // ─── Static factories ────────────────────────────────────────────────────
  /**
   * Production factory. DEPRECATED: Use RetreatManager.get() instead (Phase 1 migration).
   * This method is kept for backward compatibility but should not be used in new code.
   */
  static async create(clock, coordinator, lifecycleModule) {
    const instanceId = `legacy_${generateInstanceId()}`;
    const manager = new _RetreatManager(
      clock,
      coordinator,
      lifecycleModule ?? null,
      instanceId,
      "default",
      void 0,
      false
    );
    manager._setup();
    return manager;
  }
  /**
   * Test factory. Synchronous — creates an isolated manager instance.
   * Each test gets its own instance; use afterEach to clean up.
   *
   * Pass a partial `managerFactory` to inject test doubles for specific managers
   * without spinning up the full production stack.
   *
   * Migration from old `new RetreatManager(clock, coordinator)`:
   *   const manager = RetreatManager._createForTesting(clock, coordinator);
   */
  static _createForTesting(clock, coordinator, lifecycleModule, managerFactory) {
    const instanceId = `test_${generateInstanceId()}`;
    const factory = managerFactory ? Object.assign(
      new DefaultManagerFactory(),
      managerFactory
    ) : void 0;
    const manager = new _RetreatManager(
      clock,
      coordinator,
      lifecycleModule ?? null,
      instanceId,
      "default",
      factory,
      false
    );
    manager._setup();
    return manager;
  }
  // ─── Instance fields ─────────────────────────────────────────────────────
  /** INV-013: Assigned by the registry (create) or prefixed "test_" (_createForTesting). */
  instanceId;
  _scope;
  planes = [];
  factory;
  _bus = null;
  isInitializing = false;
  isResuming = false;
  _stopPromise = null;
  log;
  // ─── XState machine (Phase 5) ────────────────────────────────────────────
  // XState v5: Actor.send() is typed as send(event: EventObject) regardless of generics,
  // preventing typed machine events. Keeping as any until XState ships better DTS support.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _actor = null;
  // Manager team — populated in _setup(), sorted by priority
  persistenceManager;
  idempotencyManager;
  sessionManager;
  timelineManager;
  heartbeatManager;
  commandManager;
  recoveryManager;
  systemAlertManager;
  resilienceManager;
  connectivityManager;
  checkpointManager;
  lifecycleModule;
  team = [];
  /** M2-ENGINE-P0-004: Screen Wake Lock adapter — acquired on start, released on stop. */
  _wakeLock = new WakeLockController(
    () => this.getState() === "RUNNING" /* RUNNING */
  );
  /** M1-ENGINE-P1-009: Battery level monitor — started on initialize, disposed on stop. */
  _batteryMonitor = null;
  _storageMonitor = null;
  _storageUnsub;
  /** HIGH-003: store unsubscribe fn so restart doesn't double-register */
  _batteryUnsub = null;
  /** M2-ENGINE-P1-004: Total retreat duration for progressPercent computation. */
  _totalDurationSeconds = 0;
  /** INV-021: Invalidates in-flight async callbacks after stop(). */
  _lifecycleEpoch = 0;
  /** M2-ENGINE-P1-006: tracks last clock-drift voice alert to enforce 60s cooldown. */
  _lastDriftAlertAt = 0;
  /** INV-013: Instance-level destroy idempotency — same promise on repeated calls. */
  _instanceDestroyPromise = null;
  _managerSubscriptions = /* @__PURE__ */ new Map();
  _managerFactory = new DefaultManagerFactory();
  // ─── Observer interface (Phase 2) ──────────────────────────────────────────
  _snapshot = {
    status: "IDLE" /* IDLE */,
    elapsedSeconds: 0,
    pendingSession: null,
    error: null,
    lastBellFiredAt: null,
    lastTransitionBellFiredAt: null,
    completionType: null
  };
  // Memoization for getSnapshot() — useSyncExternalStore requires referential stability
  _snapshotBase = null;
  _lastMachineElapsed = -1;
  _memoizedSnapshot = this._snapshot;
  _listeners = /* @__PURE__ */ new Set();
  ALLOWED_EVENTS = new Set(
    Object.values(ENGINE_EVENTS)
  );
  // ─── Private setup (async wiring phase) ──────────────────────────────────
  /**
   * Creates the InternalBus, instantiates all managers, and wires subscriptions.
   * Called exclusively by create() and _createForTesting(), always after the
   * lock is held (or skipped for tests).
   */
  _setup() {
    this._bus = createInternalBus();
    const deps = {
      bus: this.bus,
      clock: this.clock,
      coordinator: this.coordinator,
      sessionFactory: this.factory,
      emitEvent: (event, data) => this.emitEvent(event, data),
      engineEvents: ENGINE_EVENTS,
      silent: this._silent
    };
    this.persistenceManager = this._managerFactory.createPersistenceManager(deps);
    this.idempotencyManager = this._managerFactory.createIdempotencyManager(deps);
    this.sessionManager = this._managerFactory.createSessionManager(deps);
    this.timelineManager = this._managerFactory.createTimelineManager(deps);
    this.heartbeatManager = this._managerFactory.createHeartbeatManager(deps);
    this.commandManager = this._managerFactory.createCommandManager(deps);
    this.recoveryManager = this._managerFactory.createRecoveryManager(deps);
    this.systemAlertManager = this._managerFactory.createSystemAlertManager(deps);
    this.resilienceManager = this._managerFactory.createResilienceManager(deps);
    this.connectivityManager = this._managerFactory.createConnectivityManager(deps);
    this.checkpointManager = this._managerFactory.createCheckpointManager(deps);
    this._actor = createActor(createSchedulerMachine(), {
      input: {
        heartbeatManager: this.heartbeatManager
      }
    });
    const toRetreatState = (machineState) => {
      const stateMap = {
        idle: "IDLE" /* IDLE */,
        probing: "IDLE" /* IDLE */,
        resuming: "PAUSED" /* PAUSED */,
        initializing: "INITIALIZING" /* INITIALIZING */,
        running: "RUNNING" /* RUNNING */,
        paused: "PAUSED" /* PAUSED */,
        completing: "RUNNING" /* RUNNING */,
        error: "ERROR" /* ERROR */
      };
      return stateMap[machineState] ?? "IDLE" /* IDLE */;
    };
    let lastDomainState = toRetreatState(
      this._actor.getSnapshot().value
    );
    this._actor.subscribe((snapshot) => {
      var _a, _b, _c;
      const newDomainState = toRetreatState(snapshot.value);
      const machineState = snapshot.value;
      if (machineState === "completing") {
        const ct = ((_a = snapshot.context) == null ? void 0 : _a.completionType) ?? null;
        if (ct !== null) {
          this._snapshot = { ...this._snapshot, completionType: ct };
        }
      }
      if (machineState === "initializing") {
        this._snapshot = { ...this._snapshot, completionType: null };
      }
      if (newDomainState !== lastDomainState) {
        const prevDomainState = lastDomainState;
        this.log.flow("transitionTo", {
          from: prevDomainState,
          to: newDomainState
        });
        this._snapshot = { ...this._snapshot, status: newDomainState };
        lastDomainState = newDomainState;
        this._notifyListeners();
        if (!((_b = this._bus) == null ? void 0 : _b.isClosed)) {
          this.broadcastEvent(ENGINE_EVENTS.STATE_CHANGE, {
            from: prevDomainState,
            to: newDomainState
          });
        }
        if (newDomainState === "IDLE" /* IDLE */ && !((_c = this._bus) == null ? void 0 : _c.isClosed)) {
          if (this._snapshot.completionType === "completed") {
            this.broadcastEvent("retreat:completed", {});
          }
        }
      }
    });
    this._actor.start();
    this._registerManager(this.persistenceManager);
    this._registerManager(this.checkpointManager);
    this._registerManager(this.idempotencyManager);
    this._registerManager(this.sessionManager);
    this._registerManager(this.timelineManager);
    this._registerManager(this.heartbeatManager);
    this._registerManager(this.commandManager);
    this._registerManager(this.recoveryManager);
    this._registerManager(this.systemAlertManager);
    this._registerManager(this.resilienceManager);
    this._registerManager(this.connectivityManager);
    this._wireDriftAlert();
    if (this.lifecycleModule) {
      this.lifecycleModule.onRecoveryNeeded(
        (result) => this.handleRecovery(result)
      );
    }
    this.log.info("runtime.wired", { instanceId: this.instanceId });
  }
  // ─── Bus accessor ─────────────────────────────────────────────────────────
  get bus() {
    if (!this._bus) {
      throw new Error(
        "[RetreatManager] Bus accessed before setup. Use RetreatManager.create() or _createForTesting()."
      );
    }
    return this._bus;
  }
  // ─── State predicates ──────────────────────────────────────────────────────
  get _isRunning() {
    return this._snapshot.status === "RUNNING" /* RUNNING */;
  }
  get _isPaused() {
    return this._snapshot.status === "PAUSED" /* PAUSED */;
  }
  get _isIdle() {
    return this._snapshot.status === "IDLE" /* IDLE */;
  }
  get _isCompleted() {
    return this._snapshot.status === "COMPLETED" /* COMPLETED */;
  }
  // ─── Coordinator accessor ──────────────────────────────────────────────────
  getCoordinator() {
    return this.coordinator;
  }
  // ─── Manager registration ─────────────────────────────────────────────────
  _registerManager(manager) {
    this.team.push(manager);
    this.team.sort((a, b) => a.priority - b.priority);
    if (manager.eventSubscriptions && Object.keys(manager.eventSubscriptions).length > 0) {
      const unsubscribes = [];
      Object.entries(manager.eventSubscriptions).forEach(
        ([eventName, handler]) => {
          try {
            const event = eventName;
            const callback = handler;
            this.bus.on(event, callback);
            unsubscribes.push(() => this.bus.off(event, callback));
            this.log.trace(
              "_registerManager: wired %s \u2192 %s",
              manager.name,
              eventName
            );
          } catch (err) {
            this.log.flow("_registerManager subscription failed", {
              manager: manager.name,
              event: eventName,
              error: err instanceof Error ? err.message : String(err)
            });
            this.broadcastEvent(ENGINE_EVENTS.EXECUTION_ERROR, {
              event: void 0,
              error: {
                code: "SUBSCRIPTION_ERROR",
                manager: manager.name,
                eventName,
                cause: err
              }
            });
          }
        }
      );
      if (unsubscribes.length > 0) {
        this._managerSubscriptions.set(manager.name, unsubscribes);
        this.log.flow("_registerManager: manager registered", {
          manager: manager.name,
          subscriptions: unsubscribes.length
        });
      }
    }
  }
  _rewireSubscriptions(manager) {
    if (!manager.eventSubscriptions || Object.keys(manager.eventSubscriptions).length === 0)
      return;
    const unsubscribes = [];
    Object.entries(manager.eventSubscriptions).forEach(
      ([eventName, handler]) => {
        const event = eventName;
        const callback = handler;
        this.bus.on(event, callback);
        unsubscribes.push(() => this.bus.off(event, callback));
      }
    );
    if (unsubscribes.length > 0) {
      this._managerSubscriptions.set(manager.name, unsubscribes);
    }
  }
  _unregisterManager(manager) {
    const unsubscribes = this._managerSubscriptions.get(manager.name);
    if (unsubscribes) {
      unsubscribes.forEach((unsub) => {
        try {
          unsub();
        } catch (err) {
          this.log.flow("_unregisterManager cleanup failed", {
            manager: manager.name,
            error: err instanceof Error ? err.message : String(err)
          });
        }
      });
      this._managerSubscriptions.delete(manager.name);
      this.log.flow("_unregisterManager cleaned up", {
        manager: manager.name,
        listenerCount: unsubscribes.length
      });
    }
  }
  // ─── Internal helpers ─────────────────────────────────────────────────────
  getTeamInPriorityOrder() {
    return this.team;
  }
  emitEvent(event, ...args) {
    if (!this.ALLOWED_EVENTS.has(event)) return false;
    return this.emit(event, ...args);
  }
  broadcastEvent(event, ...args) {
    this.emitEvent(event, ...args);
    this.bus.emit(event, ...args);
  }
  /** MED-005: single tick handler used by both startLoop and resumeLoop */
  _makeTickHandler() {
    return (elapsed) => {
      var _a;
      if (this._snapshot.status !== "RUNNING" /* RUNNING */) return;
      (_a = this._actor) == null ? void 0 : _a.send({ type: "TICK", elapsed: Math.floor(elapsed) });
      this._notifyListeners();
      const progressPercent = this._totalDurationSeconds > 0 ? Math.min(100, elapsed / this._totalDurationSeconds * 100) : 0;
      this.log.trace("tick", { elapsed, progressPercent });
      this.emitEvent(ENGINE_EVENTS.TICK, { elapsed, progressPercent });
      if (this.activeSession) this.activeSession.orchestrator.onTick(elapsed);
    };
  }
  // ─── Public API ───────────────────────────────────────────────────────────
  // ─── Observer interface (Phase 2) ──────────────────────────────────────────
  /**
   * Subscribe to engine state changes. Returns unsubscribe function.
   * Used by useSyncExternalStore in React.
   */
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }
  /**
   * Get current engine snapshot. Must be referentially stable across calls
   * unless state actually changed.
   */
  getSnapshot() {
    var _a, _b, _c;
    const machineElapsed = ((_c = (_b = (_a = this._actor) == null ? void 0 : _a.getSnapshot()) == null ? void 0 : _b.context) == null ? void 0 : _c.elapsedSeconds) ?? 0;
    if (this._snapshot !== this._snapshotBase || machineElapsed !== this._lastMachineElapsed) {
      this._snapshotBase = this._snapshot;
      this._lastMachineElapsed = machineElapsed;
      this._memoizedSnapshot = {
        ...this._snapshot,
        elapsedSeconds: machineElapsed
      };
    }
    return this._memoizedSnapshot;
  }
  _notifyListeners() {
    this._listeners.forEach((listener) => listener());
  }
  // ─── Public API (continued) ───────────────────────────────────────────────
  registerPlane(plane) {
    this.planes.push(plane);
    if (this.activeSession) {
      this.activeSession.orchestrator.engine.attachPlane(plane);
    }
  }
  selectRetreat(schedule) {
    var _a;
    if (((_a = this.activeSession) == null ? void 0 : _a.retreat.id) !== schedule.id) {
      this.idempotencyManager.reset();
    }
    const newSession = this.factory.create(schedule);
    const engine = newSession.orchestrator.engine;
    newSession.orchestrator.setOnComplete(() => {
      var _a2;
      if (this._snapshot.status !== "RUNNING" /* RUNNING */) return;
      this.log.flow(
        "selectRetreat onComplete: all events exhausted \u2014 natural completion"
      );
      (_a2 = this._actor) == null ? void 0 : _a2.send({ type: "COMPLETE_SCHEDULE" });
      void this.stop();
    });
    engine.setIdempotencyChecker({
      hasBeenExecuted: (id) => this.idempotencyManager.hasBeenExecuted(id)
    });
    newSession.orchestrator.setGapSkipCallback((skipped) => {
      this.broadcastEvent(ENGINE_EVENTS.GAP_SKIP_EXECUTED, skipped);
    });
    const managerContext = {
      session: newSession,
      planes: this.planes,
      clock: this.clock,
      persistenceManager: this.persistenceManager,
      engine,
      buildStateFn: () => this.buildPersistedState()
    };
    for (const manager of this.getTeamInPriorityOrder()) {
      manager.configure(managerContext);
    }
    engine.on(ENGINE_EVENTS.EXECUTION_SUCCESS, (event) => {
      var _a2;
      this.broadcastEvent(ENGINE_EVENTS.EXECUTION_SUCCESS, event);
      if (((_a2 = event == null ? void 0 : event.payload) == null ? void 0 : _a2.type) === "bell") {
        const now = Date.now();
        const isTransitionBell = typeof event.id === "string" && event.id.endsWith(":bell-after");
        this._snapshot = {
          ...this._snapshot,
          lastBellFiredAt: now,
          ...isTransitionBell ? { lastTransitionBellFiredAt: now } : {}
        };
        this._notifyListeners();
      }
    });
    engine.on(ENGINE_EVENTS.EXECUTION_ERROR, (data) => {
      this.broadcastEvent(ENGINE_EVENTS.EXECUTION_ERROR, data);
    });
    for (const plane of this.planes) {
      engine.attachPlane(plane);
    }
    this._totalDurationSeconds = schedule.days.reduce(
      (sum, day) => sum + day.containers.reduce((s, c) => s + c.durationSeconds, 0),
      0
    );
  }
  checkExistingSession(_retreatId) {
    return this.currentSession;
  }
  restore(state) {
    var _a;
    if (!state.absoluteStartTime || state.absoluteStartTime <= 0) {
      this.log.flow("restore rejected \u2014 invalid absoluteStartTime", {
        absoluteStartTime: state.absoluteStartTime
      });
      return;
    }
    this.sessionManager.hydrateFromPersistedState(state);
    const effectiveAnchor = state.savedAt ? state.savedAt - state.lastOffset * 1e3 - state.totalPausedMs : state.absoluteStartTime;
    this.heartbeatManager.restoreTimingState(
      effectiveAnchor,
      state.pausedAt,
      state.totalPausedMs,
      state.accumulatedPauseMs
    );
    this.timelineManager.setDagVersion(state.dagVersion);
    this.sessionManager.setRecoveryStrategy(state.recoveryStrategy);
    if (state.executedEventIds)
      this.idempotencyManager.restoreFromSession(state.executedEventIds);
    if (this.activeSession)
      this.activeSession.progress.offsetSeconds = state.lastOffset;
    if (this.lifecycleModule) this.lifecycleModule.setSessionState(state);
    if (state.pausedAt) {
      (_a = this._actor) == null ? void 0 : _a.send({ type: "RESTORE_PAUSED" });
    }
    this.broadcastEvent(ENGINE_EVENTS.SYNC, {
      offsetSeconds: state.lastOffset,
      absoluteStartTime: state.absoluteStartTime
    });
  }
  /**
   * INV-LIFECYCLE-001: Canonical session recovery: restore (data) → start/pause.
   * Subscribe to engine events BEFORE calling this to receive the SYNC event.
   */
  async restoreAndResume(state) {
    this.restore(state);
    if (!state.pausedAt) {
      await this.start();
    }
  }
  // ─── Private lifecycle methods (WS2 refactor) ──────────────────────────────
  _refreshBusIfClosed() {
    var _a;
    if (!((_a = this._bus) == null ? void 0 : _a.isClosed)) return;
    this._bus = createInternalBus();
    this._managerSubscriptions.clear();
    for (const manager of this.getTeamInPriorityOrder()) {
      this._rewireSubscriptions(manager);
    }
    this._wireDriftAlert();
  }
  async _bootManagers(resumeOffset) {
    for (const manager of this.getTeamInPriorityOrder()) {
      try {
        await manager.initialize();
      } catch (error) {
        const msg = `Boot sequence failed: ${manager.name}.initialize() threw: ${error.message}`;
        this.log.info("initialize failed \u2014 aborting boot sequence", {
          manager: manager.name,
          error: error.message
        });
        this._snapshot = {
          ...this._snapshot,
          error: msg,
          status: "ERROR" /* ERROR */
        };
        this._notifyListeners();
        throw new Error(msg);
      }
    }
    await this.timelineManager.generateAndHydrate(
      this.activeSession,
      resumeOffset
    );
  }
  _wireEpochGuards() {
    const epochGuardFn = () => this._lifecycleEpoch;
    const epochValidFn = this.createEpochGuard();
    if (this.clock.setEpochGuard) this.clock.setEpochGuard(epochGuardFn);
    this.heartbeatManager.setEpochGuard(epochGuardFn);
    this.persistenceManager.setEpochGuard(epochGuardFn);
    if (this.lifecycleModule) {
      this.lifecycleModule.setEpochGuard(epochValidFn);
      this.lifecycleModule.startListening();
    }
  }
  async _startBatteryMonitor() {
    var _a, _b, _c, _d;
    (_a = this._batteryUnsub) == null ? void 0 : _a.call(this);
    (_b = this._batteryMonitor) == null ? void 0 : _b.dispose();
    this._batteryMonitor = new BatteryMonitor();
    try {
      await this._batteryMonitor.start();
    } catch {
      this._batteryMonitor.dispose();
      this._batteryMonitor = null;
      return;
    }
    const batteryEpoch = this._lifecycleEpoch;
    this._batteryUnsub = this._batteryMonitor.onThresholdCrossed((event) => {
      if (batteryEpoch !== this._lifecycleEpoch) return;
      this._dispatchBatteryEvent(event);
    });
    (_c = this._storageUnsub) == null ? void 0 : _c.call(this);
    (_d = this._storageMonitor) == null ? void 0 : _d.dispose();
    this._storageMonitor = new StorageQuotaMonitor();
    this._storageMonitor.start().catch(() => {
    });
    const storageEpoch = this._lifecycleEpoch;
    this._storageUnsub = this._storageMonitor.onThresholdCrossed(
      (usagePercent) => {
        if (storageEpoch !== this._lifecycleEpoch) return;
        this.systemAlertManager.enqueue({
          text: "Storage is nearly full. Please clear space soon.",
          severity: "warning",
          dedupKey: "storage-warning"
        });
        this.emitEvent(ENGINE_EVENTS.STORAGE_QUOTA_WARNING, { usagePercent });
      }
    );
  }
  _dispatchBatteryEvent(event) {
    const alert = BATTERY_ALERT_MAP[event.threshold];
    if (alert) this.systemAlertManager.enqueue(alert);
    if (event.threshold === "LOW_WARNING") {
      this.emitEvent(ENGINE_EVENTS.LOW_BATTERY_WARNING, { level: event.level });
    } else if (event.threshold === "LOW_CRITICAL") {
      this.emitEvent(ENGINE_EVENTS.LOW_BATTERY_CRITICAL, {
        level: event.level
      });
    } else if (event.threshold === "CHARGING_RESUMED") {
      this.emitEvent(ENGINE_EVENTS.CHARGING_RESUMED, { level: event.level });
    }
  }
  _onSyncForDrift = (data) => {
    const driftMs = data == null ? void 0 : data.driftMs;
    if (!driftMs || driftMs <= DRIFT_VOICE_THRESHOLD_MS) return;
    const now = Date.now();
    if (now - this._lastDriftAlertAt < DRIFT_ALERT_COOLDOWN_MS) return;
    this._lastDriftAlertAt = now;
    this.log.flow("clock drift corrected silently", { driftMs });
  };
  _wireDriftAlert() {
    this.bus.on(ENGINE_EVENTS.SYNC, this._onSyncForDrift);
  }
  _sendTick(elapsed) {
    var _a;
    (_a = this._actor) == null ? void 0 : _a.send({ type: "TICK", elapsed: Math.floor(elapsed) });
  }
  async _stopManagers() {
    const reversed = [...this.getTeamInPriorityOrder()].reverse();
    for (const manager of reversed) {
      try {
        await manager.stop();
      } catch {
        this.log.flow("stop: manager.stop() failed \u2014 continuing", {
          manager: manager.name
        });
      }
    }
  }
  async _shutdownManagers() {
    const reversed = [...this.getTeamInPriorityOrder()].reverse();
    for (const manager of reversed) {
      try {
        await manager.shutdown();
      } catch {
        this.log.flow("stop: manager.shutdown() failed \u2014 continuing", {
          manager: manager.name
        });
      }
      try {
        this._unregisterManager(manager);
      } catch {
        this.log.flow("stop: listener cleanup failed \u2014 continuing", {
          manager: manager.name
        });
      }
    }
  }
  _teardownBattery() {
    var _a, _b, _c;
    (_a = this._batteryUnsub) == null ? void 0 : _a.call(this);
    this._batteryUnsub = null;
    (_b = this._batteryMonitor) == null ? void 0 : _b.dispose();
    this._batteryMonitor = null;
    (_c = this._storageMonitor) == null ? void 0 : _c.dispose();
    this._storageMonitor = null;
  }
  async initialize(resumeOffset = 0) {
    var _a;
    if (this.isInitializing) {
      this.log.trace("initialize ignored \u2014 already initializing");
      return;
    }
    if (this._snapshot.status !== "IDLE" /* IDLE */) {
      this.log.trace("initialize ignored \u2014 state is", {
        status: this._snapshot.status
      });
      return;
    }
    if (!this.activeSession)
      throw new Error("No active session. Call selectRetreat() first.");
    this.log.info("runtime.initializing", {
      instanceId: this.instanceId,
      resumeOffset
    });
    this._refreshBusIfClosed();
    this.isInitializing = true;
    try {
      this._wireEpochGuards();
      await this._bootManagers(resumeOffset);
      await this._startBatteryMonitor();
      (_a = this._actor) == null ? void 0 : _a.send({ type: "INIT_READY" });
      this.log.info("runtime.started", { instanceId: this.instanceId });
    } finally {
      this.isInitializing = false;
    }
  }
  async start() {
    var _a, _b, _c;
    if (this.getState() === "RUNNING" /* RUNNING */) {
      this.log.trace("start ignored \u2014 already RUNNING");
      return;
    }
    if (this.getState() === "PAUSED" /* PAUSED */) {
      this.log.flow("start redirecting to resume", { state: this.getState() });
      await this.resume();
      return;
    }
    if (this.getState() === "IDLE" /* IDLE */) {
      await this.initialize();
    }
    (_a = this._actor) == null ? void 0 : _a.send({ type: "START" });
    const syncOffset = this.heartbeatManager.startLoop(this._makeTickHandler());
    if ((_b = this.activeSession) == null ? void 0 : _b.orchestrator.eventQueue.isEmpty()) {
      this.log.flow("start: event queue empty at loop start \u2014 auto-completing");
      void this.stop();
      return;
    }
    if (syncOffset > 0) {
      (_c = this._actor) == null ? void 0 : _c.send({ type: "TICK", elapsed: Math.floor(syncOffset) });
      this._notifyListeners();
      this.emitEvent(ENGINE_EVENTS.SYNC, { offsetSeconds: syncOffset });
    }
    await this.#persistState(PersistenceTier2.CRITICAL, "start");
    void this._wakeLock.acquire();
    this.resilienceManager.startMonitoring();
  }
  sync(offsetSeconds) {
    if (!this.activeSession) {
      this.log.trace("sync ignored \u2014 no active session");
      return;
    }
    this.recoveryManager.sync(offsetSeconds, (data) => {
      this._snapshot = {
        ...this._snapshot,
        elapsedSeconds: Math.floor(data.offsetSeconds)
      };
      this._notifyListeners();
      this.emitEvent(ENGINE_EVENTS.SYNC, data);
    });
  }
  async pause() {
    var _a;
    if (this.getState() !== "RUNNING" /* RUNNING */) {
      this.log.trace("pause ignored", { state: this.getState() });
      return;
    }
    (_a = this._actor) == null ? void 0 : _a.send({ type: "PAUSE" });
    const frozenOffset = this.heartbeatManager.pauseLoop();
    await this.#persistState(PersistenceTier2.CRITICAL, "pause");
    this._snapshot = {
      ...this._snapshot,
      elapsedSeconds: Math.floor(frozenOffset)
    };
    this._notifyListeners();
    this.emitEvent(ENGINE_EVENTS.SYNC, { offsetSeconds: frozenOffset });
  }
  async resume() {
    var _a, _b;
    if (this.getState() !== "PAUSED" /* PAUSED */) {
      this.log.trace("resume ignored", { state: this.getState() });
      return;
    }
    (_a = this._actor) == null ? void 0 : _a.send({ type: "RESUME" });
    const offsetSeconds = this.heartbeatManager.resumeLoop(
      this._makeTickHandler()
    );
    await this.#persistState(PersistenceTier2.CRITICAL, "resume");
    if (offsetSeconds > 0) {
      (_b = this._actor) == null ? void 0 : _b.send({ type: "TICK", elapsed: Math.floor(offsetSeconds) });
    }
    this._notifyListeners();
    this.emitEvent(ENGINE_EVENTS.SYNC, { offsetSeconds });
  }
  seek(offsetSeconds) {
    this.recoveryManager.seek(offsetSeconds);
  }
  inject(event) {
    if (this.activeSession) this.commandManager.inject(event);
  }
  injectManualEvent(type, payload) {
    this.commandManager.injectManualEvent(type, payload);
  }
  bulkInject(events) {
    if (this.activeSession) this.commandManager.bulkInject(events);
  }
  flush() {
    if (this.activeSession) this.commandManager.flush();
  }
  dispatch(command) {
    this.commandManager.dispatch(command, this);
  }
  getState() {
    return this._snapshot.status;
  }
  getCurrentSession() {
    return this.currentSession;
  }
  // ─── Phase 3: Engine Orchestration Commands ────────────────────────────────
  /**
   * probe — Engine detects existing session at boot.
   * Sets snapshot.pendingSession if a session exists, otherwise leaves it null.
   * Called after selectRetreat() to probe for that retreat's persisted session.
   */
  async probe(retreatId, retreatVersion) {
    let id = retreatId;
    let version = retreatVersion;
    if (!id && this.activeSession) {
      id = this.activeSession.retreat.id;
      version = this.activeSession.retreat.version || "1.0.0";
    }
    if (!id) {
      this.log.trace(
        "probe: no retreat ID provided and no active session, skipping"
      );
      return;
    }
    if (!version) {
      version = "1.0.0";
    }
    try {
      const existingSession = await this.persistenceManager.loadSession(
        id,
        version
      );
      if (existingSession) {
        this.log.info(
          "probe: found existing session \u2014 lastOffset=%ds, absoluteStartTime=%d",
          existingSession.lastOffset,
          existingSession.absoluteStartTime
        );
        this._snapshot = { ...this._snapshot, pendingSession: existingSession };
        this._notifyListeners();
      } else {
        const checkpointState = this.checkpointManager.loadValidState(id);
        if (checkpointState) {
          this.log.info(
            "probe: primary storage miss \u2014 recovered from checkpoint lastOffset=%ds",
            checkpointState.lastOffset
          );
          this._snapshot = {
            ...this._snapshot,
            pendingSession: checkpointState
          };
          this._notifyListeners();
        } else {
          this.log.info("probe: no existing session found \u2014 engine stays IDLE");
        }
      }
    } catch (err) {
      this.log.info(
        "probe: error loading session: %s",
        err instanceof Error ? err.message : String(err)
      );
    }
  }
  /**
   * confirmResume — Engine owns session recovery orchestration.
   * Replaces Bridge's 3-step: initialize(offset) → subscribe → restoreAndResume(session).
   * Bridge only handles audioPlane.resume() (user gesture requirement).
   */
  async confirmResume(session) {
    if (this.isResuming) {
      this.log.trace("confirmResume ignored \u2014 already resuming (concurrent call dropped)");
      return;
    }
    if (this._stopPromise) {
      this.log.trace("confirmResume: awaiting in-flight stop()");
      await this._stopPromise;
    }
    this.isResuming = true;
    const wallClockOffset = (() => {
      if (session.savedAt) {
        return session.lastOffset;
      }
      if (session.absoluteStartTime) {
        return Math.max(
          0,
          (Date.now() - session.absoluteStartTime - (session.totalPausedMs ?? 0)) / 1e3
        );
      }
      return session.lastOffset;
    })();
    this.log.info(
      "confirmResume: wall-clock offset=%ds (saved lastOffset=%ds)",
      Math.floor(wallClockOffset),
      session.lastOffset
    );
    try {
      await this.initialize(wallClockOffset);
      await this.restoreAndResume(session);
      this._snapshot = { ...this._snapshot, pendingSession: null };
      this._notifyListeners();
    } finally {
      this.isResuming = false;
    }
  }
  /**
   * startFresh — Engine owns fresh start orchestration.
   * Replaces Bridge's 2-step: clearSession() → initialize().
   */
  async startFresh(retreatId) {
    const coordinator = this.getCoordinator();
    if (!coordinator) {
      throw new Error("Coordinator not available");
    }
    this.log.info("startFresh: clearing session and initializing");
    await coordinator.clearSession(retreatId);
    await this.initialize();
    this._snapshot = { ...this._snapshot, pendingSession: null };
    this._notifyListeners();
  }
  getActiveSession() {
    return this.activeSession;
  }
  /** M2-ENGINE-P0-008: Expose the engine's sole SystemAlertManager instance (CRIT-001). */
  getSystemAlertManager() {
    return this.systemAlertManager;
  }
  get activeSession() {
    var _a;
    return ((_a = this.sessionManager) == null ? void 0 : _a.getActiveSession()) ?? null;
  }
  get currentSession() {
    var _a;
    return ((_a = this.sessionManager) == null ? void 0 : _a.getCurrentSession()) ?? null;
  }
  getRecoveryMetadata() {
    return {
      pausedAt: this.heartbeatManager.pausedAt,
      absoluteStartTime: this.heartbeatManager.absoluteStartTime,
      recoveryStrategy: this.sessionManager.recoveryStrategy,
      dagVersion: this.timelineManager.getDagVersion()
    };
  }
  createEpochGuard() {
    const epoch = this._lifecycleEpoch;
    return () => epoch === this._lifecycleEpoch;
  }
  // ─── Stop / Destroy ───────────────────────────────────────────────────────
  async stop(options = {}) {
    if (this._isCompleted) return;
    if (this._stopPromise) return this._stopPromise;
    this._stopPromise = this._doStop(options).finally(() => {
      this._stopPromise = null;
    });
    return this._stopPromise;
  }
  async _doStop(options = {}) {
    var _a, _b, _c, _d;
    this.log.info("runtime.stopping", { instanceId: this.instanceId, keepPersistence: options.keepPersistence ?? false });
    (_a = this._actor) == null ? void 0 : _a.send({ type: "STOP" });
    this._lifecycleEpoch++;
    this.bus.off(ENGINE_EVENTS.SYNC, this._onSyncForDrift);
    this.bus.close();
    (_b = this.lifecycleModule) == null ? void 0 : _b.stopListening();
    await this._stopManagers();
    this.heartbeatManager.reset();
    this.idempotencyManager.reset();
    if (this.activeSession) {
      this.activeSession.progress.offsetSeconds = 0;
    }
    if (this.activeSession && !options.keepPersistence) {
      try {
        await this.persistenceManager.clearSession(
          this.activeSession.retreat.id
        );
        this.checkpointManager.clearCheckpoint(this.activeSession.retreat.id);
      } catch (err) {
        this.log.flow("clearSession failed during stop", {
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }
    (_c = this._actor) == null ? void 0 : _c.send({ type: "CLEANUP_DONE" });
    await this._shutdownManagers();
    for (const p of this.planes) {
      try {
        (_d = p.stopAll) == null ? void 0 : _d.call(p);
      } catch {
      }
    }
    void this._wakeLock.release();
    this._teardownBattery();
    this.log.info("runtime.stopped", { instanceId: this.instanceId });
  }
  /**
   * Download debug story — dumps machine state, snapshot, logs, and storage for production debugging.
   * Usage: window.downloadStory?.()
   */
  downloadStory() {
    var _a, _b, _c, _d;
    const snapshot = (_a = this._actor) == null ? void 0 : _a.getSnapshot();
    const machineState = snapshot == null ? void 0 : snapshot.value;
    const context = snapshot == null ? void 0 : snapshot.context;
    const story = {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      instanceId: this.instanceId,
      machine: {
        state: machineState,
        context: {
          schedule: ((_b = context == null ? void 0 : context.schedule) == null ? void 0 : _b.id) ?? null,
          pendingSession: ((_c = context == null ? void 0 : context.pendingSession) == null ? void 0 : _c.retreat.id) ?? null,
          elapsedSeconds: (context == null ? void 0 : context.elapsedSeconds) ?? 0,
          completionType: (context == null ? void 0 : context.completionType) ?? null,
          error: ((_d = context == null ? void 0 : context.error) == null ? void 0 : _d.message) ?? null
        }
      },
      snapshot: this._snapshot,
      storage: {
        localStorage: Object.fromEntries(
          Object.entries(localStorage).filter(([k]) => k.includes("retreat"))
        )
      }
    };
    const blob = new Blob([JSON.stringify(story, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `retreat-story-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  /**
   * Full teardown. Idempotent: repeated calls return the same promise.
   */
  async destroy() {
    if (!this._instanceDestroyPromise) {
      this.log.info("runtime.destroy.start", { instanceId: this.instanceId });
      this._instanceDestroyPromise = this._performDestroy();
    }
    await this._instanceDestroyPromise;
  }
  async _performDestroy() {
    try {
      await this.stop();
      const reversedTeam = [...this.getTeamInPriorityOrder()].reverse();
      for (const manager of reversedTeam) {
        try {
          await manager.shutdown();
        } catch {
          this.log.info(
            "destroy: %s.shutdown() failed \u2014 continuing teardown",
            manager.name
          );
        }
        try {
          this._unregisterManager(manager);
        } catch (e) {
          this.log.flow("destroy: listener cleanup failed", {
            manager: manager.name,
            error: e instanceof Error ? e.message : String(e)
          });
        }
      }
      if (this._managerSubscriptions.size > 0) {
        this.log.flow(
          "destroy: subscriptions remain after cleanup (possible leak)",
          {
            count: this._managerSubscriptions.size
          }
        );
        this._managerSubscriptions.clear();
      }
    } finally {
      this.log.info("runtime.destroy.complete", {
        instanceId: this.instanceId
      });
    }
  }
  // ─── Private helpers ──────────────────────────────────────────────────────
  handleRecovery(result) {
    this.recoveryManager.handleRecovery(
      result,
      (offsetSeconds) => this.sync(offsetSeconds)
    );
  }
  buildPersistedState() {
    if (!this.activeSession) return null;
    if (this.heartbeatManager.absoluteStartTime === 0) return null;
    const isPaused = this._snapshot.status === "PAUSED" /* PAUSED */;
    return this.sessionManager.buildPersistedState({
      retreatId: this.activeSession.retreat.id,
      retreatVersion: this.activeSession.retreat.version || "1.0.0",
      isPaused,
      lastOffset: this.heartbeatManager.getCurrentOffsetSeconds(isPaused),
      pausedAt: isPaused ? this.heartbeatManager.pausedAt || Date.now() : null,
      executedEventIds: this.idempotencyManager.getExecutedIds(),
      absoluteStartTime: this.heartbeatManager.absoluteStartTime,
      accumulatedPauseMs: this.heartbeatManager.accumulatedPauseOffsetMs,
      totalPausedMs: this.heartbeatManager.totalPausedMs,
      recoveryStrategy: this.sessionManager.recoveryStrategy
    });
  }
  async #persistState(tier, context) {
    const state = this.buildPersistedState();
    if (state) {
      try {
        await this.persistenceManager.saveSession(state, tier);
      } catch (err) {
        this.log.flow("persistence failed", {
          context,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
};

// src/commands/Commands.ts
import { createChildLogger as createChildLogger24 } from "@retreat/shared";
var StartCommand = class {
  log = createChildLogger24("Commands");
  execute(manager) {
    this.log.trace("StartCommand.execute");
  }
};
var PauseCommand = class {
  log = createChildLogger24("Commands");
  execute(manager) {
    this.log.trace("PauseCommand.execute");
  }
};
var SeekCommand = class {
  constructor(targetOffsetSeconds) {
    this.targetOffsetSeconds = targetOffsetSeconds;
  }
  targetOffsetSeconds;
  log = createChildLogger24("Commands");
  execute(manager) {
    this.log.trace("SeekCommand.execute", {
      targetOffset: this.targetOffsetSeconds
    });
  }
};

// src/planes/VoiceAlertPlane.ts
import { deriveDisplayName } from "@retreat/core";
import { createChildLogger as createChildLogger25 } from "@retreat/shared";
var VoiceAlertPlane = class {
  constructor(alertMgr) {
    this.alertMgr = alertMgr;
  }
  alertMgr;
  log = createChildLogger25("VoiceAlertPlane");
  execute(event) {
    const payload = event.payload;
    if ((payload == null ? void 0 : payload.type) !== "text") return;
    const contentKey = payload == null ? void 0 : payload.contentKey;
    if (!(contentKey == null ? void 0 : contentKey.endsWith(".announcement"))) return;
    const displayName = deriveDisplayName(contentKey);
    if (!displayName) return;
    const durationSeconds = payload == null ? void 0 : payload.primaryDurationSeconds;
    const min = durationSeconds && durationSeconds >= 60 ? Math.round(durationSeconds / 60) : null;
    const text = min ? `${displayName}. ${min} minutes.` : displayName;
    const safeText = text.length > 200 ? text.slice(0, 200) : text;
    const alert = {
      text: safeText,
      severity: "info"
    };
    this.log.info("announcement event \u2192 enqueue", { text: safeText, eventId: event.id });
    this.alertMgr.enqueue(alert);
    this.log.info("announcement handed to SystemAlertManager", { text: safeText });
  }
  stopAll() {
    this.alertMgr.reset();
  }
  sync(_offset) {
  }
  async prepare(_events) {
  }
};
export {
  CommandQueue,
  ENGINE_EVENTS,
  EVENT_CATEGORIES,
  ENGINE_EVENTS as EngineEvents,
  EventOrchestrator,
  ExecutionEngine,
  PLANE_TYPES,
  PauseCommand,
  PriorityEventQueue,
  RECOVERY_ACTIONS,
  RetreatManager,
  RetreatSession,
  RetreatSessionFactory,
  RetreatState,
  SeekCommand,
  StartCommand,
  SystemAlertManager,
  TimelineGenerator,
  TypedEmitter,
  VALID_PLANES,
  VoiceAlertPlane,
  WORKER_COMMANDS,
  WORKER_MESSAGES,
  isContinuousEvent,
  isDiscreteEvent,
  isValidPlane,
  isValidRecoveryAction,
  isValidWorkerCommand,
  isValidWorkerMessage
};
